import './styles/main.css';
import { tma } from './utils/tma';
import { cache } from './cache/store';
import { db, UserRole } from './api/db';
import { toast } from './utils/toast';
// import { supabase } from './api/supabase'; // Removed Supabase

import { loadTemplate, templateRegistry } from './templates/registry';
import { aiStudio } from './ai/AiStudio';
import { MiniAppBlueprint } from './ai/types';

/**
 * NEXUS PLATFORM CORE (VANILLA)
 * The engine that powers the dynamic templates.
 */

import { getContrastColor } from './utils/colors';

import { auth } from './api/firebase';
import { signInAnonymously, GoogleAuthProvider, signInWithPopup, onAuthStateChanged } from 'firebase/auth';

function escapeHTML(str: string): string {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

class NexusApp {
  private root: HTMLElement | null = null;
  private currentAlias: string | null = null;
  private config: any = null;
  public user: any = null;
  public firebaseUser: any = null;
  public role: UserRole = UserRole.USER;
  private activeTab: string = 'home';
  private forceJoinVerified: boolean = false;

  constructor() {
    this.root = document.getElementById('root');
    this.init();
    
    // Auth Listener
    onAuthStateChanged(auth, (fUser) => {
       this.firebaseUser = fUser;
       if (fUser) {
          console.log('Firebase Identity Linked:', fUser.uid);
          // Refresh role if identified as potentially admin
          this.refreshRole();
       }
    });
    
    // Attach to window for template access
    (window as any).nexus = this;
    
    // Clean popstate router for browser Back/Forward buttons
    window.addEventListener('popstate', () => {
      this.init();
    });

    // Ensure scrolling is always enabled at root
    document.documentElement.style.overflowY = 'auto';
    document.body.style.overflowY = 'auto';
    document.body.style.height = 'auto';
    document.body.style.minHeight = '100vh';
  }

  async refreshRole() {
     if (this.user?.id) {
        const appId = this.config?.id || this.config?.appId;
        this.role = await db.getUserRole(this.user.id.toString(), appId);
        this.isSuperAdmin = this.user.id.toString() === '7804637246';
     }
  }

  public clearCache(alias?: string) {
     if (alias) {
        cache.clear(`config_${alias}`);
     } else if (this.currentAlias) {
        cache.clear(`config_${this.currentAlias}`);
     } else {
        cache.clear();
     }
  }

  public togglePreviewRole() {
    const current = localStorage.getItem('nexus_preview_role') || 'user';
    const next = current === 'user' ? 'super_admin' : 'user';
    localStorage.setItem('nexus_preview_role', next);
    toast.show(`Role re-routing: rebooting profile as ${next === 'super_admin' ? 'Super Admin' : 'Isolated Tester'}...`, 'info');
    setTimeout(() => {
      window.location.reload();
    }, 1500);
  }

  async handleAdminLogin() {
     const provider = new GoogleAuthProvider();
     try {
        toast.show('Connecting to Secure Vault...', 'info');
        const result = await signInWithPopup(auth, provider);
        toast.show(`Authenticated as ${result.user.email}`, 'success');
        await this.refreshRole();
        this.renderAppShell();
     } catch (e) {
        toast.show('Authentication failed. Check popups.', 'error');
     }
  }

  private isSuperAdmin: boolean = false;

  async init() {
    // 0. Silent Auth Link
    if (!auth.currentUser) {
       await signInAnonymously(auth).catch(() => console.warn('Anonymous logic failed'));
    }

    tma.init();
    this.user = tma.getUser() || { id: 12345678, first_name: 'User', display_name: 'Anonymous User' };
    
    // Check Profile and Roles
    let profile = await db.getProfile(this.user.id.toString());
    
    if (this.user.id) {
       await db.updateProfile(this.user.id.toString(), {
          first_name: this.user.first_name,
          username: this.user.username,
          display_name: this.user.display_name,
          photo_url: this.user.photo_url
       });
    }

    // Determine current role based on context
    const initialAppId = this.config?.id || this.config?.appId;
    this.role = await db.getUserRole(this.user.id.toString(), initialAppId);
    this.isSuperAdmin = this.user.id.toString() === '7804637246';

    if (this.isSuperAdmin && auth.currentUser) {
       // Sync a secondary profile under the Firebase Auth UID so Firestore security rules evaluate isSuperAdmin() true
       await db.updateProfile(auth.currentUser.uid, {
          role: UserRole.SUPER_ADMIN,
          is_super_admin: true,
          first_name: this.user.first_name || 'Super',
          username: this.user.username || 'admin',
          display_name: 'Super Admin ' + (this.user.first_name || '')
       }).catch((err) => console.warn('Failed to sync super admin profile:', err));
    }

    // 1. Robust Routing
    const path = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);
    const aliasFromUrl = searchParams.get('alias');
    
    // Reset state
    this.config = null;
    this.currentAlias = null;

    if (path.startsWith('/app/')) {
      this.currentAlias = path.split('/app/')[1] || 'default';
    } else if (aliasFromUrl) {
      this.currentAlias = aliasFromUrl;
    }

    this.user = tma.getUser() || { id: 12345678, first_name: 'User', display_name: 'Anonymous User' };

    if (this.currentAlias) {
      this.root?.classList.add('standalone-app');
      tma.showBackButton(() => this.handleBackToDashboard());
      this.renderLoading();
      await this.loadProject();
      
      if (!this.config) {
        this.renderError('App not found. Please check the link.');
        return;
      }

      // Initialize App User Session
      try {
        const activeAppId = this.config.id || this.config.appId;
        if (activeAppId) {
          const appUser = await db.getAppUser(this.user.id.toString(), activeAppId);
          if (appUser?.status === 'banned') {
             this.renderError('ACCESS DENIED: YOUR ACCOUNT HAS BEEN SUSPENDED.');
             return;
          }
        }

        // Referral Logic
        const startParam = tma.getStartParam();
        if (startParam && startParam.includes('_')) {
           const [inviterId, appId] = startParam.split('_');
           if (appId === this.config.id && inviterId !== this.user.id.toString()) {
              const hasReferral = localStorage.getItem(`ref_${this.config.id}`);
              if (!hasReferral) {
                 await db.recordReferral(this.config.id, inviterId, this.user.id.toString(), this.config.referral_reward_amount || 100);
                 localStorage.setItem(`ref_${this.config.id}`, inviterId);
                 toast.show('Neural link verified: Referral established.', 'success');
              }
           }
        }
      } catch (e) { console.error('Session init failed'); }

      // Global Announcement Protocol
      try {
         const globalAnn = await db.getGlobalSetting('announcement').catch(() => null);
         if (globalAnn && globalAnn.active && globalAnn.text) {
            const viewedKey = `ann-global-${globalAnn.id}`;
            if (!window.localStorage.getItem(viewedKey)) {
               this.showAnnouncement(globalAnn.text);
               window.localStorage.setItem(viewedKey, 'true');
            }
         }
      } catch (e) {
         console.warn('Failed to load global announcement:', e);
      }

      // Project Announcement Protocol
      if (this.config?.announcement && !window.localStorage.getItem(`ann-project-${this.config.id}-${this.config.announcement.length}`)) {
         this.showAnnouncement(this.config.announcement);
         window.localStorage.setItem(`ann-project-${this.config.id}-${this.config.announcement.length}`, 'true');
      }

      if (this.config?.status === 'paused') {
        this.renderMaintenance();
        return;
      }

      const requiresJoin = this.config?.force_join_channels?.length > 0;
      if (requiresJoin && !this.forceJoinVerified) {
        this.renderForceJoin();
      } else {
        this.renderAppShell();
      }
    } else if (path === '/factory') {
      this.root?.classList.remove('standalone-app');
      tma.showBackButton(() => this.handleBackToDashboard());
      this.renderFactory();
    } else {
      this.root?.classList.remove('standalone-app');
      tma.hideBackButton();
      this.renderDashboard();
    }
  }

  async loadProject() {
    console.log(`📡 Connecting to app: ${this.currentAlias}`);
    try {
      let config = cache.get(`config_${this.currentAlias}`);
      if (!config) {
        // Always use Firestore now
        try {
          const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('DB Timeout')), 3000));
          config = await Promise.race([db.getAppConfig(this.currentAlias!), timeoutPromise]);
        } catch (dbError) { 
          console.warn('DB error, checking local storage'); 
        }

        if (!config) {
          const custom = JSON.parse(localStorage.getItem('nexus_custom_vessels') || '{}');
          if (custom[this.currentAlias!]) {
            config = custom[this.currentAlias!];
          }
        }
        if (config) cache.set(`config_${this.currentAlias}`, config, true);
      }
      this.config = config;
      this.applyTheme();
    } catch (error) {
      console.error('Boot Error:', error);
      this.renderError('Failed to sync data with the server.');
    }
  }

  applyTheme() {
    const theme = this.config?.theme || {};
    const root = document.documentElement;
    
    // Core Colors
    const primary = theme.primaryColor || '#6366f1';
    const bg = theme.bgColor || '#f8fafc';
    
    root.style.setProperty('--primary-color', primary);
    root.style.setProperty('--secondary-color', theme.secondaryColor || '#a855f7');
    root.style.setProperty('--accent-color', theme.accentColor || '#f43f5e');
    root.style.setProperty('--bg-color', bg);
    
    // Auto contrast
    const primaryContrast = getContrastColor(primary);
    const bgContrast = getContrastColor(bg);
    root.style.setProperty('--on-primary', primaryContrast === 'white' ? '#ffffff' : '#0f172a');
    root.style.setProperty('--on-bg', bgContrast === 'white' ? '#ffffff' : '#0f172a');
    root.style.setProperty('--text-muted', bgContrast === 'white' ? 'rgba(255,255,255,0.6)' : 'rgba(15,23,42,0.6)');
    
    // Gradients
    const gradStart = primary;
    const gradEnd = theme.secondaryColor || '#a855f7';
    root.style.setProperty('--premium-gradient', `linear-gradient(135deg, ${gradStart} 0%, ${gradEnd} 100%)`);
    root.style.setProperty('--loading-gradient', theme.loadingGradient || `linear-gradient(135deg, ${theme.primaryColor || '#1e293b'} 0%, ${theme.secondaryColor || '#0f172a'} 100%)`);

    const isDarkBg = bgContrast === 'white';
    const secondaryBtnBg = isDarkBg ? 'rgba(255,255,255,0.1)' : '#f1f5f9';
    const secondaryBtnText = isDarkBg ? '#ffffff' : '#0f172a';
    root.style.setProperty('--secondary-btn-bg', secondaryBtnBg);
    root.style.setProperty('--secondary-btn-text', secondaryBtnText);

    // Typography
    const fontFamily = theme.fontFamily || 'Outfit';
    root.style.setProperty('--font-family', `'${fontFamily}', sans-serif`);
    
    document.body.className = `min-h-screen font-custom scroll-fix`;
    document.body.style.backgroundColor = theme.bgColor || '#f8fafc';
    document.body.style.color = isDarkBg ? '#ffffff' : '#0f172a';
    document.body.style.fontFamily = `'${fontFamily}', sans-serif`;
  }

  renderForceJoin() {
    if (!this.root || !this.config) return;
    const channels = this.config.force_join_channels || [];
    
    this.root.innerHTML = `
      <div class="absolute inset-0 p-8 flex flex-col items-center justify-center space-y-10 bg-white z-[100] overflow-y-auto">
        <div class="w-24 h-24 rounded-[2.5rem] bg-indigo-600 flex items-center justify-center text-white shadow-3xl shadow-indigo-200 animate-float flex-shrink-0">
           <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
        </div>
        <div class="text-center space-y-3">
           <h2 class="text-4xl font-black italic uppercase tracking-tighter text-slate-900">Follow to Access</h2>
           <p class="text-[10px] text-slate-400 font-mono uppercase tracking-[0.3em] max-w-[250px] mx-auto">Join required channels to proceed through the neural link.</p>
        </div>
        <div class="w-full space-y-3 max-w-[90%]">
           ${channels.map((ch: string, index: number) => `
             <div class="premium-card !p-5 flex items-center justify-between border-slate-100 bg-slate-50/50">
                <div class="flex items-center gap-4">
                   <div class="w-10 h-10 rounded-2xl bg-indigo-600/10 flex items-center justify-center text-indigo-600 font-black tracking-tighter italic text-xs">TG</div>
                   <p class="text-[10px] font-black italic text-slate-800 uppercase tracking-tight">${ch}</p>
                </div>
                <button onclick="window.nexus.handleJoin('${ch}')" class="px-5 py-3 bg-white border border-slate-200 text-slate-900 rounded-xl text-[9px] font-black uppercase italic tracking-widest shadow-sm active:scale-95 transition-all">Join Now</button>
             </div>
           `).join('')}
        </div>
        <div class="w-full space-y-3">
           <button onclick="window.nexus.handleVerifyJoin()" class="btn-primary w-full py-5 text-[11px] !tracking-[0.4em] shadow-2xl shadow-indigo-200">VERIFY MEMBERSHIP</button>
           <p class="text-[8px] text-slate-300 font-mono uppercase text-center tracking-widest">Bot Token: ${this.config.bot_token ? 'Synchronized' : 'Missing'}</p>
        </div>
      </div>
    `;
  }

  handleJoin(channel: string) {
    tma.hapticFeedback('medium');
    const link = channel.startsWith('@') ? `https://t.me/${channel.substring(1)}` : 
                 channel.startsWith('https://t.me/') ? channel : `https://t.me/${channel}`;
    tma.openLink(link);
  }

  async handleVerifyJoin() {
    tma.hapticFeedback('light');
    const verifyBtn = document.querySelector('button[onclick="window.nexus.handleVerifyJoin()"]') as HTMLButtonElement;
    if (verifyBtn) {
       verifyBtn.disabled = true;
       verifyBtn.innerHTML = '<span class="animate-spin mr-2">●</span> SYNCHRONIZING...';
    }
    
    // Simulate verification delay for "manual unlock" feel on static site
    await new Promise(resolve => setTimeout(resolve, 3000));

    try {
      const channels = this.config.force_join_channels || [];
      if (channels.length === 0) {
         this.forceJoinVerified = true;
         this.renderAppShell();
         return;
      }

      // If we have a bot token, try real verification
      if (this.config.bot_token) {
         try {
           const results = await Promise.all(channels.map((ch: string) => 
             db.checkTelegramMembership(this.user.id.toString(), ch, this.config.bot_token)
           ));
           const failed = results.filter(r => !r.joined);
           if (failed.length === 0) {
             this.forceJoinVerified = true;
             toast.show('Neural Link Established. Access Granted.', 'success');
             this.renderAppShell();
             return;
           }
         } catch (e) { console.warn('Live API check failed'); }
      }
      
      // Manual/Simulated unlock fallback as requested for static site behavior
      this.forceJoinVerified = true;
      toast.show('Manual Verification Synchronized.', 'success');
      this.renderAppShell();
      
    } catch (e) {
      toast.show('Verification relay error. Please retry.', 'error');
      if (verifyBtn) {
         verifyBtn.disabled = false;
         verifyBtn.innerHTML = 'RETRY VERIFICATION';
      }
    }
  }

  renderAppShell() {
    if (!this.root) return;
    this.root.innerHTML = '';
    
    const shell = document.createElement('div');
    shell.id = 'app-shell';
    shell.className = 'flex flex-col min-h-[100dvh] w-full relative bg-slate-50';
    
    const content = document.createElement('div');
    content.id = 'shell-content';
    content.className = 'flex-1 overflow-y-auto pb-32 scroll-smooth scroll-fix bg-inherit px-1';
    
    const nav = document.createElement('nav');
    nav.className = 'absolute bottom-4 left-4 right-4 h-20 glass rounded-[2.5rem] px-4 flex items-center justify-around z-[90] shadow-2xl shadow-indigo-100/50 border border-white/50 backdrop-blur-xl overflow-x-auto no-scrollbar scrolling-touch animate-in fade-in slide-in-from-bottom-5 duration-700';
    
    // Admin Security Check
    const isOwner = this.user.id.toString() === this.config?.owner_id?.toString();
    const canAccessAdmin = isOwner || this.role === UserRole.SUPER_ADMIN || this.isSuperAdmin;
    
    // Base tabs
    const allTabs = [
      { id: 'home', icon: 'M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z', label: 'Home' },
      { id: 'tasks', icon: 'M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2', label: 'Tasks' },
      { id: 'spin', icon: 'M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z', label: 'Spin' },
      { id: 'refer', icon: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2', label: 'Invite' },
      { id: 'stats', icon: 'M12 20V10M18 20V4M6 20v-4', label: 'Stats' },
      { id: 'wallet', icon: 'M20 12V8H6a2 2 0 0 1 2-2h12V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2h-3', label: 'Wallet' },
      { id: 'analytics', icon: 'M3 3v18h18M18 17V9M13 17V5M8 17v-3', label: 'Data' },
      { id: 'logs', icon: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z', label: 'Logs' },
      { id: 'profile', icon: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', label: 'Profile' }
    ];

    // Filter and customize tabs based on config
    let tabs = allTabs;
    if (this.config?.tabs_config && Array.isArray(this.config.tabs_config)) {
      tabs = this.config.tabs_config
        .filter((t: any) => t.enabled !== false)
        .map((t: any) => {
          const original = allTabs.find(at => at.id === t.id);
          return {
            ...original,
            label: t.label || original?.label,
            icon: t.icon || original?.icon,
            id: t.id
          };
        });
    }

    if (canAccessAdmin) {
      tabs.push({ id: 'admin', icon: 'M12 11V3m0 0L9 6m3-3 3 3M8 7H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-2', label: 'Admin' });
    }

    const renderNav = () => {
      nav.innerHTML = tabs.map(tab => `
        <div onclick="window.nexus.switchTab('${tab.id}')" class="flex-shrink-0 flex flex-col items-center justify-center w-14 h-14 rounded-2xl transition-all duration-300 ${this.activeTab === tab.id ? 'text-indigo-600 scale-110' : 'text-slate-400 opacity-60'}">
           <div class="relative">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="${tab.icon}"/>
              </svg>
              ${this.activeTab === tab.id ? '<div class="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-indigo-600 rounded-full"></div>' : ''}
           </div>
           <span class="text-[7px] font-black uppercase tracking-widest mt-1.5">${tab.label}</span>
        </div>
      `).join('');
    };

    renderNav();
    
    const templateId = (this.config?.template_id || this.config?.templateId || 'hybrid_rewards_x').toLowerCase();
    const isFullSPA = templateId !== 'hybrid_rewards_x';
    const showPlatformNav = !isFullSPA && this.activeTab !== 'admin';

    if (!showPlatformNav) {
       content.className = 'flex-1 overflow-y-auto pb-6 scroll-smooth bg-inherit px-1';
    }

    shell.appendChild(content);
    if (showPlatformNav) {
      shell.appendChild(nav);
    }
    this.root.appendChild(shell);

    // Check if running in browser mockup/preview instead of real TMA
    const hasTelegramContext = (window as any).Telegram?.WebApp?.initDataUnsafe?.user;
    if (!hasTelegramContext) {
      const activeRole = localStorage.getItem('nexus_preview_role') || 'user';
      const isSuper = activeRole === 'super_admin';
      
      const devBar = document.createElement('div');
      devBar.className = 'fixed top-4 right-4 z-[99999] pointer-events-auto bg-slate-900/95 text-white border border-slate-700 p-3 rounded-2xl shadow-xl flex flex-col gap-1.5 text-[9px] font-mono select-none backdrop-blur-md max-w-[180px] animate-in fade-in duration-300';
      devBar.innerHTML = `
         <div class="flex items-center gap-1.5 font-bold text-[#c0ff4d]">
            <span class="inline-block w-1.5 h-1.5 rounded-full ${isSuper ? 'bg-[#c0ff4d] animate-pulse' : 'bg-blue-400'}"></span>
            <span>PREVIEW IDENTITY</span>
         </div>
         <div class="text-slate-355 leading-none">
            Role: <span class="text-white font-black uppercase text-[8px]">${isSuper ? 'Super Admin' : 'Tester'}</span>
         </div>
         <div class="text-slate-355 leading-none truncate">
            Name: <span class="text-white font-medium">${this.user?.first_name || 'Anonymous'}</span>
         </div>
         <div class="text-slate-400 text-[8px] leading-none">
            ID: <span class="text-slate-300">${this.user?.id}</span>
         </div>
         <button onclick="window.nexus.togglePreviewRole()" class="mt-1 px-2.5 py-1.5 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-white font-sans text-[8px] font-black uppercase tracking-wider text-center transition-all cursor-pointer">
            Switch to ${isSuper ? 'Tester' : 'Super Admin'}
         </button>
      `;
      this.root.appendChild(devBar);
    }
    
    this.renderActiveTab();
  }

  switchTab(id: string) {
    if (this.activeTab === id) return;
    tma.hapticFeedback('soft');
    this.activeTab = id;
    
    // Ensure scrolling reset when tab changes
    window.scrollTo(0, 0);
    this.renderAppShell();
  }

  async renderActiveTab() {
    const container = document.getElementById('shell-content');
    if (!container) return;

    // Security Check for Admin Tab
    const isOwner = this.user.id.toString() === this.config?.owner_id?.toString();
    const canAccessAdmin = isOwner || this.role === UserRole.SUPER_ADMIN || this.isSuperAdmin;

    if (this.activeTab === 'admin' && !canAccessAdmin) {
      this.activeTab = 'home';
      this.renderAppShell();
      toast.show('Access Denied: Restricted Area.', 'error');
      return;
    }
    container.innerHTML = `
      <div class="px-8 py-10 space-y-6 animate-in fade-in duration-500">
         <div class="skeleton h-8 w-1/3 mb-4"></div>
         <div class="skeleton h-40 w-full mb-6"></div>
         <div class="space-y-4">
            <div class="skeleton h-20 w-full"></div>
            <div class="skeleton h-20 w-full"></div>
            <div class="skeleton h-20 w-full"></div>
         </div>
      </div>
    `;

    const templateId = (this.config?.template_id || this.config?.templateId || 'hybrid_rewards_x').toLowerCase();
    
    // Humanize config for templates
    const normalizedConfig = {
      ...this.config,
      id: this.config?.id || this.config?.appId || 'default',
      currency_name: this.config?.currency_name || this.config?.currencyName || 'PTS',
      template_id: templateId
    };

    try {
      const loader = this.activeTab === 'admin' 
        ? () => import('./templates/admin_panel')
        : () => loadTemplate(templateId);
      
      const module = await loader();
      const template = module.default;
      
      // Fetch fresh user app data for templates
      const appUser: any = (await db.getAppUser(this.user.id.toString(), normalizedConfig.id).catch(() => ({ balance: 0 }))) || { balance: 0 };
      
      // Clear container and reset scroll
      container.innerHTML = '';
      container.scrollTop = 0;
      
      // We pass the appraisal balance as the 4th argument for templates that need it
      await template(container, normalizedConfig, this.user, this.activeTab === 'home' ? (appUser.balance || 0) : this.activeTab);
    } catch (e) {
      console.error('Tab loading error:', e);
      toast.show('Failed to load page: ' + this.activeTab, 'error');
    }
  }

  async handleTaskSubmission(taskId: string, title: string, appId: string, type: 'url' | 'text' | 'image' | 'global' = 'url', link?: string) {
    tma.hapticFeedback('medium');
    
    // Safety check to prevent any interaction or submission if approved or pending
    try {
      const submissions = await db.getTaskSubmissions(appId).catch(() => []);
      const userSub = (submissions || []).find(s => s.task_id === taskId && s.user_id === this.user!.id.toString());
      if (userSub && (userSub.status === 'approved' || userSub.status === 'pending')) {
        toast.show('This mission already has a record of submission or completion and is locked.', 'warning');
        return;
      }
    } catch (err) {
      console.warn(err);
    }
    
    // Redirect logic first if link exists
    if (link) {
      tma.openLink(link.startsWith('http') ? link : `https://${link}`);
      toast.show('Redirecting for mission protocol. Capture evidence.', 'info');
    }

    const modalId = `modal-${Date.now()}`;
    const showModal = () => {
      if (document.getElementById(modalId)) return;
      const modal = document.createElement('div');
      modal.id = modalId;
      modal.className = 'fixed inset-0 z-[1000] flex items-center justify-center p-6 bg-slate-900/90 backdrop-blur-md animate-in fade-in duration-300';
      
      const fileId = `file-upload-${Date.now()}`;
      
      modal.innerHTML = `
        <div class="premium-card max-w-[92%] w-full p-8 space-y-6 bg-white border-none shadow-3xl">
           <div class="space-y-1 text-center">
              <h3 class="text-2xl font-black italic uppercase tracking-tighter text-slate-800">Mission Evidence</h3>
              <p class="text-[9px] text-slate-400 font-mono uppercase tracking-widest italic">${title}</p>
           </div>
           
           <div class="space-y-5">
              <div class="space-y-3">
                 <label class="text-[10px] font-black uppercase text-slate-500">Upload Screenshot Proof</label>
                 <label class="flex flex-col items-center justify-center w-full h-44 border-2 border-dashed border-slate-200 rounded-3xl cursor-pointer hover:bg-slate-50 transition-all">
                    <div id="upload-placeholder" class="flex flex-col items-center justify-center pt-5 pb-6 text-center px-4">
                       <svg class="w-8 h-8 mb-4 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/></svg>
                       <p class="mb-2 text-[10px] font-black uppercase text-slate-500 tracking-widest">Select From Gallery</p>
                       <p class="text-[7px] text-slate-400 font-mono uppercase">Capture proof of completion</p>
                    </div>
                    <img id="preview-img" class="hidden w-full h-full object-cover rounded-[1.5rem]" />
                    <input id="${fileId}" type="file" class="hidden" accept="image/*" />
                 </label>
                 <p id="file-name" class="text-[8px] text-center font-mono text-indigo-500 truncate px-4"></p>
              </div>
              
              <button id="submit-btn" class="btn-primary w-full py-5 text-[11px] !tracking-[0.2em]">COMPLETE MISSION</button>
              <button onclick="document.getElementById('${modalId}').remove()" class="w-full text-[9px] font-black uppercase tracking-widest text-slate-400 py-2">Go Back</button>
           </div>
        </div>
      `;
      
      document.body.appendChild(modal);

      const fileInput = modal.querySelector(`#${fileId}`) as HTMLInputElement;
      const preview = modal.querySelector('#preview-img') as HTMLImageElement;
      const placeholder = modal.querySelector('#upload-placeholder') as HTMLElement;

      fileInput?.addEventListener('change', (e: any) => {
         const file = e.target.files[0];
         if (file) {
            modal.querySelector('#file-name')!.textContent = file.name;
            const reader = new FileReader();
            reader.onload = (re) => {
               preview.src = re.target?.result as string;
               preview.classList.remove('hidden');
               placeholder.classList.add('hidden');
            };
            reader.readAsDataURL(file);
         }
      });

      modal.querySelector('#submit-btn')?.addEventListener('click', async () => {
         const submitBtn = modal.querySelector('#submit-btn') as HTMLButtonElement;
         submitBtn.disabled = true;
         submitBtn.innerHTML = '<span class="animate-spin text-white">●</span> UPLOADING...';

         try {
            const fileInput = modal.querySelector(`#${fileId}`) as HTMLInputElement;
            const file = fileInput?.files?.[0];
            
            if (!file) {
               toast.show('Proof Screenshot Required.', 'warning');
               submitBtn.disabled = false;
               submitBtn.innerText = 'COMPLETE MISSION';
               return;
            }

            const res = await this.uploadToTelegram(file);
            const proofUrl = res.success ? res.url : 'manual_review_pending';
            
            await db.submitTask(appId, taskId, this.user!.id.toString(), { 
               type: 'image', 
               value: 'screenshot_submitted',
               proof_url: proofUrl,
               is_global: type === 'global'
            });
            
            toast.show('Transmission stored. Admin verification pending.', 'success');
            modal.remove();
            this.renderActiveTab();
         } catch (e) {
            toast.show('Relay synchronization failure.', 'error');
            submitBtn.disabled = false;
            submitBtn.innerText = 'COMPLETE MISSION';
         }
      });
    };

    if (link) {
      setTimeout(showModal, 1500); 
    } else {
      showModal();
    }
  }

  async handleAddAuthorizedAdmin() {
    const id = prompt('Enter Telegram ID to authorize:');
    if (!id) return;
    toast.show('Updating authority list...', 'info');
    try {
       const authorizedAdmins = await db.getGlobalSetting('authorized_super_admins') || {};
       authorizedAdmins[id] = true;
       await db.setGlobalSetting('authorized_super_admins', authorizedAdmins);
       toast.show('Access granted to ' + id, 'success');
       this.renderPlatformContent();
    } catch (e) { toast.show('Update failed.', 'error'); }
  }

  async handleRemoveAuthorizedAdmin(id: string) {
    if (!confirm('Revoke access for ' + id + '?')) return;
    try {
       const authorizedAdmins = await db.getGlobalSetting('authorized_super_admins') || {};
       delete authorizedAdmins[id];
       await db.setGlobalSetting('authorized_super_admins', authorizedAdmins);
       toast.show('Access revoked.', 'warning');
       this.renderPlatformContent();
    } catch (e) { toast.show('Update failed.', 'error'); }
  }

  async handleCreateGlobalTask() {
    const title = prompt('Global Mission Title:');
    if (!title) return;
    const reward = prompt('Reward:');
    const link = prompt('Link:');
    const mandatory = confirm('Is this task mandatory for all withdrawals?');
    try {
       await db.createGlobalTask({ title, reward: parseFloat(reward || '0'), link, is_mandatory: mandatory });
       toast.show('Global mission broadcasted.', 'success');
       this.renderPlatformContent();
    } catch (e) { toast.show('Creation failed.', 'error'); }
  }

  async handleDeleteGlobalTask(id: string) {
    if (!confirm('Remove this global mission?')) return;
    await db.deleteGlobalTask(id);
    this.renderPlatformContent();
  }

  async uploadToTelegram(file: File) {
     if (!this.config.bot_token) {
        return { success: true, url: 'manual_verification_pending' };
     }
     
     const formData = new FormData();
     formData.append('photo', file);
     // Use log_channel_id if available, fallback to the first force_join channel
     const chatId = this.config.bot_config?.log_channel_id || this.config.force_join_channels?.[0] || 'admin';
     formData.append('chat_id', chatId);
     formData.append('caption', `MISSION PROOF\nUser: ${this.user.first_name} (${this.user.id})\nApp: ${this.config.name}\nTimestamp: ${new Date().toISOString()}`);

     try {
        const res = await fetch(`https://api.telegram.org/bot${this.config.bot_token}/sendPhoto`, {
           method: 'POST',
           body: formData
        });
        const data = await res.json();
        if (data.ok) {
           return { success: true, url: data.result.photo?.[data.result.photo.length - 1]?.file_id || 'verified' };
        }
        return { success: false };
     } catch (e) {
        return { success: false };
     }
  }

  async handleWithdrawalModal() {
    tma.hapticFeedback('medium');
    
    const config = this.config;
    const appUser = await db.getAppUser(this.user.id.toString(), config.id).catch(() => ({ balance: 0 }));
    const balance = Number(appUser?.balance || 0);
    
    const withdrawalConfig = config.withdrawal_config || {};
    const minAmount = Number(withdrawalConfig.min_amount || config.min_withdrawal || 500);
    const isRestricted = withdrawalConfig.restricted || config.withdrawal_restricted || false;
    
    // 1. Check if disabled by creator
    if (isRestricted) {
       toast.show('Withdrawals are temporarily disabled by the admin.', 'error');
       return;
    }

    if (balance < minAmount) {
       toast.show(`Insufficient balance. Minimum is ${minAmount} ${config.currency_name || 'PTS'}`, 'warning');
       return;
    }

    // 2. Check Global Task Eligibility
    const eligibility = await db.checkWithdrawalEligibility(this.user.id.toString(), config.id);
    if (!eligibility.eligible) {
       this.showRequirementModal('Verification Required', eligibility.missingTasks || []);
       return;
    }

    const modal = document.createElement('div');
    const modalId = `modal-${Date.now()}`;
    modal.id = modalId;
    modal.className = 'absolute inset-0 z-[1000] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-md animate-in fade-in duration-300';
    
    const modes: string[] = withdrawalConfig.modes || ['crypto'];
    const activeMode: string = modes.includes('crypto') ? 'crypto' : modes[0] || 'bank';

    modal.innerHTML = `
      <div id="withdrawal-panel" class="premium-card max-w-[92%] w-full p-8 space-y-6 bg-white border-none shadow-3xl">
         <div class="space-y-1">
            <h3 class="text-2xl font-black italic uppercase tracking-tighter text-slate-800">Withdraw Funds</h3>
            <p class="text-[9px] text-slate-400 font-mono uppercase tracking-widest">Select Method & Enter Details</p>
         </div>
         
         <div class="space-y-5">
            <div class="flex p-1 bg-slate-100 rounded-2xl gap-1">
               ${modes.map((m: string) => `
                  <button id="mode-${m}" class="flex-1 py-3 rounded-xl text-[8px] font-black uppercase tracking-widest transition-all ${m === activeMode ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-400'}">${m}</button>
               `).join('')}
            </div>

            <div id="withdrawal-fields" class="space-y-4"></div>
            
            <div class="p-4 bg-indigo-50 rounded-2xl border border-indigo-100">
               <p class="text-[8px] font-black uppercase text-indigo-400 mb-1">Instruction</p>
               <p class="text-[9px] text-indigo-600 leading-relaxed font-medium italic">${config.withdrawal_config?.instructions || 'Withdrawals are processed in 24-48 hours.'}</p>
            </div>

            <button id="withdraw-confirm-btn" class="btn-primary w-full py-5 text-[11px] !tracking-[0.2em]">INITIATE TRANSFER</button>
            <button onclick="document.getElementById('${modalId}').remove()" class="w-full text-[9px] font-black uppercase tracking-widest text-slate-400 py-2 text-center">Cancel</button>
         </div>
      </div>
    `;
    
    document.body.appendChild(modal);

    const switchMode = (mode: string) => {
       const fields = modal.querySelector('#withdrawal-fields') as HTMLElement;
       const buttons = modal.querySelectorAll('[id^="mode-"]');
       buttons.forEach(b => b.classList.remove('bg-white', 'shadow-sm', 'text-indigo-600'));
       buttons.forEach(b => b.classList.add('text-slate-400'));
       modal.querySelector(`#mode-${mode}`)?.classList.add('bg-white', 'shadow-sm', 'text-indigo-600');
       modal.querySelector(`#mode-${mode}`)?.classList.remove('text-slate-400');

       if (mode === 'bank') {
          fields.innerHTML = `
             <div class="space-y-4">
                <input id="w-bank-name" type="text" placeholder="Bank Name" class="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-xs font-black outline-none focus:border-indigo-500">
                <input id="w-bank-acc" type="text" placeholder="Account Number" class="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-xs font-black outline-none focus:border-indigo-500">
                <input id="w-bank-user" type="text" placeholder="Account Name" class="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-xs font-black outline-none focus:border-indigo-500">
             </div>
          `;
       } else {
          fields.innerHTML = `
             <div class="space-y-4">
                <input id="w-crypto-addr" type="text" placeholder="Wallet Address (TON/BEP20)" class="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-xs font-black outline-none focus:border-indigo-500">
             </div>
          `;
       }
       (window as any).currentWithdrawMode = mode;
    };

    modes.forEach(mode => {
       modal.querySelector(`#mode-${mode}`)?.addEventListener('click', () => switchMode(mode));
    });

    switchMode(activeMode);

    modal.querySelector('#withdraw-confirm-btn')?.addEventListener('click', async () => {
       const panel = document.getElementById('withdrawal-panel');
       const mode = (window as any).currentWithdrawMode || 'crypto';
       let details: any = {};
       
       if (mode === 'bank') {
          const bankName = (panel?.querySelector('#w-bank-name') as HTMLInputElement)?.value;
          const accNum = (panel?.querySelector('#w-bank-acc') as HTMLInputElement)?.value;
          const accName = (panel?.querySelector('#w-bank-user') as HTMLInputElement)?.value;
          
          if (!bankName || !accNum || !accName) {
             toast.show('Please fill all bank details.', 'warning');
             return;
          }
          details = { bank_name: bankName, account_number: accNum, account_holder: accName };
       } else {
          const addr = (panel?.querySelector('#w-crypto-addr') as HTMLInputElement)?.value;
          if (!addr || addr.length < 8) {
             toast.show('Please enter a valid wallet address.', 'warning');
             return;
          }
          details = { address: addr };
       }

       try {
          const btn = modal.querySelector('#withdraw-confirm-btn') as HTMLButtonElement;
          btn.disabled = true;
          btn.innerHTML = 'TRANSITIONING...';

          await db.requestWithdrawal(config.id, this.user.id.toString(), {
             amount: balance,
             method: mode,
             details
          });
          
          await db.updateBalance(this.user.id.toString(), config.id, -balance);
          toast.show('Withdrawal initiated!', 'success');
          modal.remove();
          this.renderActiveTab();
       } catch (e) {
          toast.show('Network relay failure.', 'error');
          modal.remove();
       }
    });
  }

  showRequirementModal(title: string, list: string[]) {
     const modal = document.createElement('div');
     modal.className = 'fixed inset-0 z-[2000] flex items-center justify-center p-6 bg-slate-900/80 backdrop-blur-md animate-in fade-in duration-300';
     modal.innerHTML = `
        <div class="premium-card max-w-xs w-full p-8 space-y-6 bg-white shadow-3xl text-center">
           <div class="w-16 h-16 bg-red-600 rounded-[2rem] mx-auto flex items-center justify-center text-white shadow-2xl">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>
           </div>
           <div class="space-y-2">
              <h3 class="text-xl font-black italic uppercase tracking-tighter text-slate-800">${title}</h3>
              <p class="text-[9px] text-slate-500 font-bold uppercase tracking-widest leading-relaxed">Complete these mandatory platform tasks to unlock withdrawals:</p>
           </div>
           <div class="space-y-2">
              ${list.map(t => `
                 <div class="py-2 px-4 bg-slate-50 rounded-xl text-[8px] font-black uppercase text-slate-600 border border-slate-100">${t}</div>
              `).join('')}
           </div>
           <button onclick="this.closest('.fixed').remove(); window.nexus.switchTab('tasks');" class="btn-primary w-full py-4 text-[9px] !rounded-2xl">Go to Tasks</button>
        </div>
     `;
     document.body.appendChild(modal);
  }

  showAnnouncement(msg: string) {
     const modal = document.createElement('div');
     modal.className = 'fixed inset-0 z-[2000] flex items-center justify-center p-6 bg-slate-900/80 backdrop-blur-md animate-in fade-in duration-300';
     modal.innerHTML = `
        <div class="premium-card max-w-xs w-full p-8 space-y-6 bg-white shadow-3xl text-center">
           <div class="w-16 h-16 bg-indigo-600 rounded-[2rem] mx-auto flex items-center justify-center text-white shadow-2xl">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
           </div>
           <div class="space-y-2">
              <h3 class="text-xl font-black italic uppercase tracking-tighter text-slate-800">Notice</h3>
              <p class="text-[10px] text-slate-500 font-bold uppercase italic leading-relaxed">${msg}</p>
           </div>
           <button onclick="this.closest('.fixed').remove()" class="btn-primary w-full py-4 text-[9px] !rounded-2xl">Dismiss</button>
        </div>
     `;
     document.body.appendChild(modal);
  }

  async handleTelegramTask(taskId: string, link: string, appId: string) {
    tma.hapticFeedback('medium');
    
    // Safety check to prevent any interaction or submission if approved or pending
    try {
      const submissions = await db.getTaskSubmissions(appId).catch(() => []);
      const userSub = (submissions || []).find(s => s.task_id === taskId && s.user_id === this.user!.id.toString());
      if (userSub && (userSub.status === 'approved' || userSub.status === 'pending')) {
        toast.show('This mission already has a record of submission or completion and is locked.', 'warning');
        return;
      }
    } catch (err) {
      console.warn(err);
    }
    
    const tasks = (await db.getTasks(appId)) || [];
    const globalTasks = (await db.getGlobalTasks()) || [];
    const allTasks = [...tasks, ...globalTasks];
    const task = allTasks.find(t => t.id === taskId);
    if (!task) return;

    if (link) {
       tma.openLink(link.startsWith('http') ? link : `https://t.me/${link.replace('@', '')}`);
    }

    // NEW: Force screenshot upload for Telegram tasks as well if requested
    await this.handleTaskSubmission(taskId, task.title, appId, task.is_global ? 'global' : 'url', link);
  }

  handleAdminMode(alias: string) {
    tma.hapticFeedback('heavy');
    window.history.pushState({}, '', `/app/${alias}`);
    this.currentAlias = alias;
    this.activeTab = 'admin';
    this.init();
  }

  async handleSpinResult(appId: string, amount: number) {
    try {
      tma.hapticFeedback('success');
      // Always store history
      await db.updateBalance(this.user!.id.toString(), appId, amount);
      toast.show(`Detected Yield: +${amount} ${this.config?.currency_name || 'PTS'}`, 'success');
      
      // Refresh balance in UI if possible
      const balEl = document.querySelector('.glowing-balance');
      if (balEl) {
         const current = parseFloat(balEl.textContent || '0');
         balEl.textContent = (current + amount).toFixed(2);
      }
    } catch (e) {
      console.error(e);
      toast.show('Yield synchronization failed.', 'error');
    }
  }

  handleInterface(alias: string) {
    toast.show(`Synchronizing with ${alias.replace('_', ' ')} module...`, 'info');
    tma.hapticFeedback('medium');
    window.history.pushState({}, '', `/app/${alias}`);
    this.currentAlias = alias;
    this.activeTab = 'home';
    this.init();
  }

  handleBackToDashboard() {
    tma.hapticFeedback('medium');
    tma.hideBackButton();
    if (window.location.pathname !== '/' || window.location.search) {
      window.history.pushState({}, '', '/');
    }
    this.currentAlias = null;
    this.config = null;
    this.activeTab = 'home';
    this.init();
  }

  private platformTab: string = 'discovery';
  private superTab: string = 'config';

  async renderDashboard() {
    if (!this.root) return;
    
    this.root.innerHTML = `
      <div class="absolute inset-0 pointer-events-none opacity-40 overflow-hidden bg-white">
         <div class="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-[160px] animate-pulse"></div>
      </div>

      <div class="px-4 py-6 md:p-8 space-y-8 md:space-y-12 relative z-10 max-w-2xl mx-auto pb-32">
        <header class="flex justify-between items-center sm:items-end px-1">
           <div class="space-y-1">
              <h1 class="text-[9px] font-black uppercase tracking-[0.4em] text-indigo-500">Platform Management</h1>
              <p class="text-2xl sm:text-4xl font-black italic uppercase tracking-tighter text-slate-800">Marketplace</p>
           </div>
           <div class="w-12 h-12 rounded-2xl bg-slate-900 flex items-center justify-center text-white shadow-xl">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
           </div>
        </header>

        <!-- Platform Tabs -->
        <div class="flex gap-1.5 p-1 bg-slate-100 rounded-2xl overflow-x-auto scrolling-touch no-scrollbar">
           <button onclick="window.nexus.switchPlatformTab('discovery')" class="flex-1 min-w-[60px] whitespace-nowrap py-3 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${this.platformTab === 'discovery' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}">Explore</button>
           <button onclick="window.nexus.handleOpenAiStudio()" class="flex-1 min-w-[70px] whitespace-nowrap py-3 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-sm hover:opacity-90">✨ AI Studio</button>
           <button onclick="window.nexus.switchPlatformTab('marketplace')" class="flex-1 min-w-[60px] whitespace-nowrap py-3 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${this.platformTab === 'marketplace' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}">Templates</button>
           <button onclick="window.nexus.switchPlatformTab('projects')" class="flex-1 min-w-[60px] whitespace-nowrap py-3 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${this.platformTab === 'projects' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}">Projects</button>
           <button onclick="window.nexus.switchPlatformTab('notifications')" class="flex-1 min-w-[60px] whitespace-nowrap py-3 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${this.platformTab === 'notifications' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}">Notices</button>
           ${this.isSuperAdmin ? `
             <button onclick="window.nexus.switchPlatformTab('super')" class="flex-1 min-w-[60px] whitespace-nowrap py-3 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${this.platformTab === 'super' ? 'bg-white text-red-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}">Super</button>
           ` : ''}
        </div>

        <div id="platform-content" class="animate-in fade-in slide-in-from-bottom-4 duration-500 w-full">
           <div class="flex flex-col items-center justify-center py-20 gap-4">
              <div class="w-10 h-10 border-2 border-slate-200 border-t-indigo-500 rounded-full animate-spin"></div>
              <p class="text-[8px] font-black uppercase tracking-widest text-slate-400">Loading Dashboard...</p>
           </div>
        </div>
      </div>
    `;

    this.renderPlatformContent();
  }

  async switchPlatformTab(tab: string) {
    if (tab === 'super' && !this.isSuperAdmin) {
      toast.show('Access Denied: Root Authority Required', 'error');
      return;
    }
    if (this.platformTab === tab) return;
    tma.hapticFeedback('soft');
    this.platformTab = tab;
    this.renderDashboard();
  }

  switchSuperTab(tab: string) {
    if (this.superTab === tab) return;
    tma.hapticFeedback('soft');
    this.superTab = tab;
    this.renderPlatformContent();
  }

  async handleClearSystemLogs() {
    if (!confirm('Are you absolutely sure you want to clear all system logs?')) return;
    tma.hapticFeedback('warning');
    const ok = await db.clearSystemErrors();
    if (ok) {
      toast.show('Logs cleared successfully', 'success');
      this.renderPlatformContent();
    } else {
      toast.show('Failed to wipe log database', 'error');
    }
  }

  async renderPlatformContent() {
    const container = document.getElementById('platform-content');
    if (!container) return;

    try {
      if (this.platformTab === 'discovery') {
        const apps = (await db.getPublicApps().catch(() => [])) || [];
        const movers = await db.getGlobalLeaderboard().catch(() => []);

        container.innerHTML = `
          <div class="space-y-8">
             <!-- AI Studio Hero Banner -->
             <div class="p-6 rounded-3xl bg-gradient-to-r from-indigo-900 via-purple-900 to-slate-900 border border-indigo-500/30 text-white shadow-2xl space-y-4 relative overflow-hidden">
                <div class="space-y-1.5 relative z-10">
                   <div class="flex items-center gap-2">
                     <span class="px-2.5 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 text-[8px] font-black uppercase tracking-widest">Neural Architect</span>
                     <span class="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-[7px] font-bold uppercase">Zero-API Required</span>
                   </div>
                   <h2 class="text-xl sm:text-2xl font-black italic uppercase tracking-tight text-white">Create Telegram Mini Apps with AI</h2>
                   <p class="text-xs text-slate-300 max-w-md leading-relaxed">Chat your earnings app idea. The neural engine creates custom themes, missions, rewards, and auto-hosts with a single click.</p>
                </div>
                <button onclick="window.nexus.handleOpenAiStudio()" class="relative z-10 px-6 py-3.5 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded-2xl text-[9px] font-black uppercase tracking-widest shadow-xl shadow-indigo-500/20 active:scale-95 transition-all">
                   ✨ Launch AI Architect & Host
                </button>
             </div>
             <div class="space-y-4">
                <div class="flex justify-between items-center px-1">
                   <h3 class="text-[9px] font-black uppercase tracking-[0.4em] text-slate-400">Elite Technicians</h3>
                </div>
                <div class="flex gap-4 overflow-x-auto scrolling-touch no-scrollbar pb-2">
                   ${movers.filter(m => m.profile).map((m: any, i: number) => `
                     <div class="flex-shrink-0 flex flex-col items-center gap-2 group">
                        <div class="w-14 h-14 rounded-[2rem] bg-indigo-600/5 p-1 border border-indigo-100 group-hover:scale-105 transition-all">
                           <img src="${m.profile?.photo_url || 'https://ui-avatars.com/api/?name=' + (m.profile?.first_name || 'U')}" class="w-full h-full object-cover rounded-[1.8rem]" />
                        </div>
                        <div class="text-center">
                           <p class="text-[7px] font-black uppercase text-slate-800">${m.profile?.first_name}</p>
                           <p class="text-[6px] font-mono text-indigo-500 uppercase">${m.count} Vessels</p>
                        </div>
                     </div>
                   `).join('')}
                </div>
             </div>

             <div class="space-y-4">
                <div class="flex justify-between items-center px-1">
                   <h3 class="text-[9px] font-black uppercase tracking-[0.4em] text-slate-400">Live Network Deployments</h3>
                </div>
                <div class="grid grid-cols-1 gap-4">
                ${apps.length === 0 ? '<p class="text-center py-20 text-[9px] font-black uppercase text-slate-300">Scanning frequency... No vessels detected.</p>' : ''}
                ${apps.map((app: any) => `
                  <div class="premium-card p-4 flex items-center justify-between group hover:bg-white transition-all bg-slate-50/50">
                     <div class="flex items-center gap-4">
                        <div class="w-12 h-12 rounded-2xl bg-white border border-slate-100 flex items-center justify-center text-indigo-600 shadow-sm group-hover:scale-105 transition-all">
                           <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg>
                        </div>
                        <div>
                           <p class="text-xs font-black uppercase italic tracking-tight text-slate-800">${app.name}</p>
                           <p class="text-[7px] text-indigo-500 font-mono uppercase tracking-widest mt-0.5">${app.template_id?.toUpperCase() || 'CORE'}</p>
                        </div>
                     </div>
                     <button onclick="window.nexus.handleInterface('${app.alias}')" class="px-5 py-2.5 bg-white border border-slate-100 rounded-xl text-[8px] font-black uppercase tracking-widest shadow-sm hover:bg-slate-50">Synchronize</button>
                  </div>
                `).join('')}
             </div>
          </div>
        `;
      } else if (this.platformTab === 'marketplace') {
        container.innerHTML = `
          <div class="space-y-6">
             <div class="flex justify-between items-center px-1">
                <h3 class="text-[9px] font-black uppercase tracking-[0.4em] text-slate-400">Architecture Gallery</h3>
             </div>
             <div class="grid grid-cols-1 gap-6">
                ${templateRegistry.map(tpl => `
                    <div class="premium-card p-0 overflow-hidden group">
                       <div class="h-32 bg-slate-950 relative flex items-center justify-center overflow-hidden">
                          <img src="${tpl.preview}" class="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:scale-110 transition-all duration-700" />
                          <div class="absolute inset-0 bg-gradient-to-t from-slate-950 to-transparent"></div>
                          <h4 class="relative z-20 text-2xl font-black italic text-white uppercase tracking-tighter shadow-sm">${tpl.name}</h4>
                       </div>
                       <div class="p-4 space-y-4">
                        <div class="flex justify-between items-start">
                           <div>
                              <p class="text-[9px] font-black text-indigo-600 uppercase tracking-widest">${tpl.category}</p>
                              <p class="text-sm font-bold text-slate-800">${tpl.desc}</p>
                           </div>
                        </div>
                        <div class="grid grid-cols-2 gap-3">
                           <button onclick="window.nexus.handlePreviewTemplate('${tpl.id}')" class="px-3 py-4 bg-slate-50 text-slate-800 rounded-2xl text-[9px] font-black uppercase tracking-widest border border-slate-100 italic transition-all hover:bg-white">Preview</button>
                           <button onclick="window.nexus.handleNewVessel('${tpl.id}')" class="btn-primary w-full py-4 text-[9px]">Create Project</button>
                        </div>
                     </div>
                  </div>
                `).join('')}
             </div>
          </div>
        `;
      } else if (this.platformTab === 'projects') {
        const projects = (await db.getOwnedApps(this.user.id.toString()).catch(() => [])) || [];
        container.innerHTML = `
          <div class="space-y-8">
             <div class="flex justify-between items-center px-1">
                <h3 class="text-[9px] font-black uppercase tracking-[0.4em] text-slate-400">Active Deployments</h3>
             </div>
             
             ${projects.length === 0 ? `
               <div class="premium-card p-8 text-center space-y-6 bg-slate-50/50 border-dashed border-slate-200">
                  <div class="w-16 h-16 bg-white rounded-[2rem] mx-auto flex items-center justify-center text-slate-300 shadow-sm">
                     <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                  </div>
                  <div class="space-y-2">
                     <p class="text-[10px] font-black uppercase italic text-slate-800">Project list empty</p>
                     <p class="text-[8px] text-slate-400 font-mono uppercase max-w-[200px] mx-auto">You have no active vessels deployed on the Nexus network.</p>
                  </div>
                  <button onclick="window.nexus.switchPlatformTab('marketplace')" class="px-8 py-3 bg-indigo-600 text-white rounded-2xl text-[9px] font-black uppercase tracking-widest">Browse Marketplace</button>
               </div>
             ` : projects.map((p: any) => `
               <div class="premium-card p-4 group bg-white border border-slate-100 shadow-xl shadow-indigo-100/20">
                  <div class="flex justify-between items-start mb-6 px-1">
                     <div class="flex items-center gap-4">
                        <div class="w-12 h-12 rounded-2xl bg-slate-900 flex items-center justify-center text-white">
                           <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                        </div>
                        <div>
                           <p class="text-xs font-black uppercase italic text-slate-800">${p.name}</p>
                           <p class="text-[7px] text-slate-400 font-mono uppercase tracking-[0.2em] mt-1">ID: ${p.alias}</p>
                        </div>
                     </div>
                     <div class="px-3 py-1 ${p.status === 'paused' ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'} rounded-lg">
                        <p class="text-[7px] font-black uppercase tracking-widest ${p.status === 'active' ? 'animate-pulse' : ''}">${p.status === 'paused' ? 'Paused' : 'Live'}</p>
                     </div>
                  </div>
                  
                  <div class="grid grid-cols-2 gap-2 mb-2">
                     <button onclick="window.nexus.handleInterface('${p.alias}')" class="py-3 bg-slate-50 text-slate-800 rounded-xl text-[9px] font-black uppercase tracking-widest border border-slate-100">Open App</button>
                     <button onclick="window.nexus.handleAdminMode('${p.alias}')" class="py-3 bg-indigo-600 text-white rounded-xl text-[9px] font-black uppercase tracking-widest">Admin Panel</button>
                  </div>
                  
                  <div class="grid grid-cols-3 gap-2">
                     <button onclick="window.nexus.handleEditProject('${p.id}')" class="py-2.5 bg-white border border-slate-100 rounded-xl text-[8px] font-black uppercase text-slate-500">Edit Config</button>
                     <button onclick="window.nexus.handlePauseProject('${p.id}', '${p.status}')" class="py-2.5 bg-white border border-slate-100 rounded-xl text-[8px] font-black uppercase text-slate-500">${p.status === 'paused' ? 'Resume' : 'Pause'}</button>
                     <button onclick="window.nexus.handleDeleteProject('${p.id}')" class="py-2.5 bg-white border border-slate-100 rounded-xl text-[8px] font-black uppercase text-red-400">Delete</button>
                  </div>
               </div>
             `).join('')}
          </div>
        `;
      } else if (this.platformTab === 'notifications') {
        await this.renderNotifications();
      } else if (this.platformTab === 'super') {
        const authorizedAdmins = await db.getGlobalSetting('authorized_super_admins').catch(() => null) || {};
        const gTasks = (await db.getGlobalTasks().catch(() => [])) || [];
        const allApps = (await db.getAllApps().catch(() => [])) || [];
        const currentLockId = await db.getGlobalSetting('super_admin_id').catch(() => null) || '';
        const devBotToken = await db.getGlobalSetting('dev_broadcast_bot_token').catch(() => null) || '';
        const activeSuperTemplate = this.selectedSuperTemplateId || 'duxbux';
        const superCreds = await db.getTemplateCredentials(activeSuperTemplate).catch(() => null) || {};
        const systemLogs = this.superTab === 'logs' ? await db.getSystemErrors() : [];
        
        container.innerHTML = `
          <div class="space-y-6 pb-32">
             <!-- Super Sub-nav -->
             <div class="flex p-1 bg-slate-100 rounded-2xl gap-1 overflow-x-auto no-scrollbar">
                <button onclick="window.nexus.switchSuperTab('config')" class="flex-1 whitespace-nowrap py-3 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${this.superTab === 'config' ? 'bg-white text-red-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}">Core Settings</button>
                <button onclick="window.nexus.switchSuperTab('logs')" class="flex-1 whitespace-nowrap py-3 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${this.superTab === 'logs' ? 'bg-white text-red-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}">System Logs</button>
             </div>

             ${this.superTab === 'logs' ? `
                <div class="premium-card p-6 bg-red-50/50 border-red-100 border-2 space-y-4">
                   <div class="flex items-center justify-between">
                      <div class="flex items-center gap-3">
                         <div class="w-10 h-10 rounded-xl bg-red-600 text-white flex items-center justify-center shadow-md">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                         </div>
                         <div>
                            <h3 class="text-xs font-black uppercase italic text-red-700">System Logs</h3>
                            <p class="text-[7px] text-red-400 font-mono uppercase tracking-widest mt-0.5">Real-time platform error diagnostics</p>
                         </div>
                      </div>
                      <button onclick="window.nexus.handleClearSystemLogs()" class="px-4 py-2 bg-white hover:bg-slate-50 border border-red-100 rounded-xl text-[8px] font-black uppercase tracking-widest shadow-sm">Clear All</button>
                   </div>
                      
                   <div class="space-y-3">
                      ${systemLogs.length === 0 ? `
                        <div class="p-12 text-center bg-white rounded-2xl border border-red-50 space-y-2">
                           <p class="text-[9px] font-black uppercase text-slate-400 tracking-widest">No errors recorded</p>
                           <p class="text-[7px] text-slate-300 font-mono italic">Platform operations running cleanly.</p>
                        </div>
                      ` : systemLogs.map((log: any) => `
                        <div class="p-3 bg-white rounded-xl border border-red-100/60 space-y-2 shadow-sm">
                           <div class="flex justify-between items-start">
                              <div class="flex items-center gap-2 flex-wrap">
                                 <span class="px-2 py-0.5 bg-red-50 text-red-600 text-[6px] font-black font-mono rounded uppercase tracking-wider border border-red-100">${log.context}</span>
                                 <span class="text-[7px] font-mono text-slate-400 uppercase tracking-widest font-black">${log.app_id}</span>
                              </div>
                              <p class="text-[7px] text-slate-300 font-mono">${log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}</p>
                           </div>
                           <p class="text-[8px] font-mono font-medium text-slate-800 bg-slate-50 p-2 rounded-lg border border-slate-100 break-all leading-relaxed">${escapeHTML(log.error)}</p>
                           ${log.user_email || log.user_id ? `
                             <div class="flex gap-2 text-[7px] text-slate-400 font-mono uppercase">
                                <span>Dispatcher: ${log.user_email || log.user_id}</span>
                             </div>
                           ` : ''}
                        </div>
                      `).join('')}
                   </div>
                </div>
             ` : `
                <!-- Control Node -->
                <div class="premium-card p-6 bg-red-50/50 border-red-100 border-2 space-y-6">
                   <div class="flex items-center justify-between">
                      <div class="flex items-center gap-4">
                         <div class="w-12 h-12 rounded-2xl bg-red-600 text-white flex items-center justify-center shadow-xl">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                         </div>
                         <div>
                            <h3 class="text-xs font-black uppercase italic text-red-700">Root Authority</h3>
                            <p class="text-[8px] text-red-500 font-mono uppercase tracking-widest">Global Master Configuration</p>
                         </div>
                      </div>
                      <button onclick="window.nexus.handleSuperBroadcast()" class="p-3 bg-white border border-red-100 rounded-xl text-red-600">
                         <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                      </button>
                   </div>
  
                   <!-- Admin Access Lock -->
                   <div class="space-y-2 border-b border-red-100 pb-4">
                      <p class="text-[8px] font-black uppercase text-red-400 tracking-widest">Super Admin Lock</p>
                      <div class="flex gap-2">
                          <input id="super-admin-lock" type="text" value="${currentLockId}" placeholder="Enter Telegram ID..." class="flex-1 bg-white border border-red-100 p-4 rounded-xl text-xs font-black outline-none focus:border-red-300">
                          <button onclick="window.nexus.handleUpdateSuperAdminId()" class="px-6 py-4 bg-red-600 text-white rounded-xl text-[9px] font-black uppercase tracking-widest shadow-lg">Lock ID</button>
                      </div>
                      <p class="text-[7px] text-red-400 font-mono uppercase italic">Restricts Super Admin tab and global tasks to this specific Telegram ID.</p>
                   </div>

                   <!-- Developer Broadcast Bot Token Setup -->
                   <div class="space-y-2 border-b border-red-100 pb-4">
                      <p class="text-[8px] font-black uppercase text-red-400 tracking-widest">Developer Broadcast Bot Token</p>
                      <div class="flex gap-2">
                          <input id="super-dev-bot-token" type="password" value="${devBotToken}" placeholder="Enter Bot Token for Developer broadcast..." class="flex-1 bg-white border border-red-100 p-4 rounded-xl text-xs font-black outline-none focus:border-red-300 font-mono">
                          <button onclick="window.nexus.handleUpdateDevBotToken()" class="px-6 py-4 bg-red-600 text-white rounded-xl text-[9px] font-black uppercase tracking-widest shadow-lg">Save Token</button>
                      </div>
                      <p class="text-[7px] text-red-400 font-mono uppercase italic">This token is used exclusively to send broadcast telegram messages directly to mini-app owners (creators).</p>
                   </div>
  
                   <!-- Admin Management -->
                   <div class="space-y-3">
                      <div class="flex justify-between items-center px-1">
                         <p class="text-[8px] font-black uppercase text-red-400 tracking-widest">Authorized Delegates</p>
                         <button onclick="window.nexus.handleAddAuthorizedAdmin()" class="text-[8px] font-black uppercase text-red-600 underline">Add Admin</button>
                      </div>
                      <div class="space-y-2">
                         ${Object.entries(authorizedAdmins).map(([id, active]) => `
                            <div class="flex items-center justify-between p-3 bg-white rounded-xl border border-red-50">
                               <p class="text-[9px] font-mono text-slate-800 uppercase">${id}</p>
                               <button onclick="window.nexus.handleRemoveAuthorizedAdmin('${id}')" class="text-red-400">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/></svg>
                               </button>
                            </div>
                         `).join('')}
                      </div>
                   </div>
  
                   <!-- Global Tasks -->
                   <div class="space-y-3 border-t border-red-100 pt-4">
                      <div class="flex justify-between items-center px-1">
                         <p class="text-[8px] font-black uppercase text-red-400 tracking-widest">Platform Missions</p>
                         <button onclick="window.nexus.handleCreateGlobalTask()" class="text-[8px] font-black uppercase text-red-600 underline">Create Global</button>
                      </div>
                      <div class="space-y-2">
                         ${gTasks.map((t: any) => `
                            <div class="flex items-center justify-between p-3 bg-white rounded-xl border border-red-50">
                               <div>
                                  <p class="text-[9px] font-black text-slate-800 uppercase italic">${t.title}</p>
                                  <p class="text-[7px] text-red-400 font-mono uppercase">+${t.reward} • ${t.is_mandatory ? 'Mandatory' : 'Optional'}</p>
                               </div>
                               <button onclick="window.nexus.handleDeleteGlobalTask('${t.id}')" class="text-red-400">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/></svg>
                               </button>
                            </div>
                         `).join('')}
                      </div>
                   </div>
                </div>
  
                <!-- Isolated DB Panel (Super Admin Locked) -->
                <div class="premium-card p-6 bg-red-50/50 border-red-100 border-2 space-y-4">
                   <div class="flex items-center gap-3">
                      <div class="w-10 h-10 rounded-xl bg-red-600 text-white flex items-center justify-center shadow-md">
                         <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5V19A9 3 0 0 0 21 19V5"/><path d="M3 12A9 3 0 0 0 21 12"/></svg>
                      </div>
                      <div>
                         <h3 class="text-xs font-black uppercase text-red-700 leading-none">Template Database Isolation</h3>
                         <p class="text-[7px] text-red-400 font-mono uppercase tracking-wider mt-1">Configure isolated firestore per template</p>
                      </div>
                   </div>
  
                   <div class="space-y-3">
                      <div>
                         <label class="text-[8px] font-black uppercase text-red-500 tracking-widest block mb-1">Select Template Module</label>
                         <select onchange="window.nexus.handleSuperTemplateDbSelect(this.value)" class="w-full bg-white border border-red-100 p-3 rounded-xl text-xs font-black outline-none focus:border-red-350 transition-all font-mono">
                            <option value="duxbux" ${activeSuperTemplate === 'duxbux' ? 'selected' : ''}>DuxBux Template</option>
                            <option value="earnhub" ${activeSuperTemplate === 'earnhub' ? 'selected' : ''}>EarnHub Template</option>
                            <option value="sparkbnb" ${activeSuperTemplate === 'sparkbnb' ? 'selected' : ''}>SparkBNB Template</option>
                         </select>
                      </div>
  
                      <div class="grid grid-cols-2 gap-2">
                         <div>
                            <label class="text-[8px] font-black uppercase text-red-400 tracking-widest block mb-1">Firestore API Key</label>
                            <input id="super-db-apikey" type="text" value="${superCreds.apiKey || ''}" placeholder="AIzaSy..." class="w-full bg-white border border-red-100 p-3 rounded-xl text-xs font-mono outline-none focus:border-red-300 font-mono">
                         </div>
                         <div>
                            <label class="text-[8px] font-black uppercase text-red-400 tracking-widest block mb-1">Firestore Project ID</label>
                            <input id="super-db-projectid" type="text" value="${superCreds.projectId || ''}" placeholder="isolated-project-id" class="w-full bg-white border border-red-100 p-3 rounded-xl text-xs font-mono outline-none focus:border-red-300 font-mono">
                         </div>
                      </div>
  
                      <div class="grid grid-cols-2 gap-2">
                         <div>
                            <label class="text-[8px] font-black uppercase text-red-400 tracking-widest block mb-1">Auth Domain (Optional)</label>
                            <input id="super-db-authdomain" type="text" value="${superCreds.authDomain || ''}" placeholder="project-id.firebaseapp.com" class="w-full bg-white border border-red-100 p-3 rounded-xl text-xs font-mono outline-none focus:border-red-300 font-mono">
                         </div>
                         <div>
                            <label class="text-[8px] font-black uppercase text-red-400 tracking-widest block mb-1">Storage Bucket (Optional)</label>
                            <input id="super-db-bucket" type="text" value="${superCreds.storageBucket || ''}" placeholder="project-id.appspot.com" class="w-full bg-white border border-red-100 p-3 rounded-xl text-xs font-mono outline-none focus:border-red-300 font-mono">
                         </div>
                      </div>
  
                      <button onclick="window.nexus.handleSaveSuperTemplateDb('${activeSuperTemplate}')" class="w-full py-4 bg-red-600 text-white rounded-xl text-[9px] font-black uppercase tracking-widest shadow-lg active:scale-[0.98] transition-all">
                         Save Database Credentials
                      </button>
                   </div>
                </div>
  
                <div class="space-y-4">
                   <div class="flex justify-between items-center px-1">
                      <h3 class="text-[9px] font-black uppercase tracking-[0.4em] text-slate-400">Network Projects (${allApps.length})</h3>
                   </div>
                   <div class="space-y-3">
                      ${allApps.map((app: any) => `
                        <div class="premium-card p-4 flex items-center justify-between border-slate-100 hover:bg-white transition-all shadow-sm">
                           <div class="flex items-center gap-3">
                              <div class="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center text-[10px] font-black italic">V</div>
                              <div>
                                 <p class="text-[10px] font-black uppercase italic text-slate-800">${app.name}</p>
                                 <p class="text-[7px] text-slate-400 font-mono uppercase">Owner: ${app.owner_id}</p>
                              </div>
                           </div>
                           <div class="flex gap-2">
                              <button onclick="window.nexus.handleInterface('${app.alias}')" class="p-2 bg-slate-50 rounded-xl text-slate-400 hover:bg-slate-100 transition-colors">
                                 <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                              </button>
                              <button onclick="window.nexus.handleAdminMode('${app.alias}')" class="p-2 bg-indigo-50 rounded-xl text-indigo-400 hover:bg-indigo-100 transition-colors">
                                 <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                              </button>
                              <button onclick="window.nexus.handleDeleteProject('${app.id}')" class="p-2 bg-red-50 rounded-xl text-red-400 hover:bg-red-100 transition-colors">
                                 <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                              </button>
                           </div>
                        </div>
                      `).join('')}
                   </div>
                </div>
             `}
          </div>
        `;
      }
    } catch (e) {
      container.innerHTML = `<p class="text-center py-20 text-[9px] font-black uppercase text-red-400 italic">Sync Failure. Check Terminal.</p>`;
    }
  }

   handleNewVessel(templateId: string) {
    tma.hapticFeedback('heavy');
    (window as any).selectedTemplate = templateId;
    (window as any).editingAppId = null;
    window.history.pushState({}, '', '/factory');
    this.init();
  }

  async renderNotifications() {
     const container = document.getElementById('platform-content');
     if (!container) return;
     
     try {
        const notes = await db.getCreatorNotifications(this.user.id.toString());
        container.innerHTML = `
           <div class="space-y-6">
              <div class="flex justify-between items-center px-1">
                 <h3 class="text-[9px] font-black uppercase tracking-[0.4em] text-slate-400">Secure Notifications</h3>
              </div>
              
              <div class="space-y-3">
                 ${notes.length === 0 ? `
                   <div class="premium-card p-8 text-center bg-slate-50/50 border-dashed border-slate-200">
                      <p class="text-[9px] font-black uppercase text-slate-400 tracking-widest italic">Clear Frequency</p>
                   </div>
                 ` : notes.map((n: any) => `
                    <div class="premium-card p-5 bg-white border border-slate-100 italic transition-all animate-in slide-in-from-left-4">
                       <div class="flex gap-4 items-start">
                          <div class="w-10 h-10 rounded-2xl ${n.priority === 'high' ? 'bg-red-50 text-red-600' : 'bg-indigo-50 text-indigo-600'} flex-shrink-0 flex items-center justify-center">
                             <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                          </div>
                          <div class="space-y-1">
                             <p class="text-[10px] font-black uppercase text-slate-800">${n.title}</p>
                             <p class="text-[9px] text-slate-500 font-medium leading-relaxed">${n.message}</p>
                             <p class="text-[7px] text-slate-300 font-mono mt-2">${n.created_at?.toDate ? n.created_at.toDate().toLocaleString() : 'Just now'}</p>
                          </div>
                       </div>
                    </div>
                 `).join('')}
              </div>
           </div>
        `;
     } catch (e) {
        container.innerHTML = '<p class="text-center py-20 text-[9px] font-black uppercase text-red-400 italic">Notification relay error</p>';
     }
  }

  handleSettings() {
    tma.hapticFeedback('medium');
    toast.show('Settings are managed in the Admin Panel.', 'info');
  }

  handleVerify() {
    tma.hapticFeedback('medium');
    toast.show('Verifying connection...', 'info');
    setTimeout(() => {
      toast.show('Secure connection established. No issues found.', 'success');
    }, 1500);
  }

  handleInvite() {
    tma.hapticFeedback('medium');
    let url = '';
    if (this.config?.referral_base_url) {
      const base = this.config.referral_base_url;
      const separator = base.includes('?') ? '&' : '?';
      const refParam = base.includes('t.me') ? 'startapp' : 'start';
      url = `${base}${separator}${refParam}=${this.user.id}_${this.config.id}`;
    } else {
      const botUser = this.config?.bot_config?.bot_username || 'NexusPlatformBot';
      const cleanBotUser = botUser.replace('@', '').trim();
      url = `https://t.me/${cleanBotUser}?start=${this.user.id}_${this.config.id}`;
    }
    tma.shareLink(url, `Join ${this.config?.name || 'this app'} and start earning rewards!`);
  }

  handleViewProof(url: string) {
    tma.openLink(url);
  }

  handleEditTask(id: string) {
    toast.show('Node adjustment protocol initiates in Admin Panel.', 'info');
  }

  showGlobalAnnouncement(ann: any) {
    const modal = document.createElement('div');
    modal.className = 'absolute inset-0 z-[1000] flex items-center justify-center p-4 bg-slate-900/90 backdrop-blur-md animate-in fade-in duration-500';
    modal.innerHTML = `
      <div class="premium-card p-10 max-w-[92%] w-full bg-slate-800 border-indigo-500/30 border-2 space-y-6 text-center shadow-[0_0_50px_rgba(99,102,241,0.2)]">
         <div class="w-16 h-16 bg-indigo-600 rounded-[2rem] mx-auto flex items-center justify-center text-white shadow-lg shadow-indigo-500/50">
            <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
         </div>
         <div class="space-y-2">
            <h3 class="text-xl font-black italic uppercase tracking-tighter text-white">Announcement</h3>
            <p class="text-[10px] text-slate-400 font-mono uppercase tracking-[0.2em]">New message from admin</p>
         </div>
         <p class="text-xs text-slate-300 italic leading-relaxed border-y border-slate-700/50 py-6 font-medium">${ann.text}</p>
         <button onclick="localStorage.setItem('ann-${ann.id}', 'viewed'); this.closest('.fixed').remove();" class="w-full py-4 bg-indigo-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest active:scale-95 transition-all shadow-xl shadow-indigo-500/20">Acknowledge</button>
      </div>
    `;
    document.body.appendChild(modal);
  }

  async handleSuperBroadcast() {
     if (!this.isSuperAdmin) {
        toast.show('Access Denied: Root Authority Required', 'error');
        return;
     }
     this.handleShowBroadcastModal();
  }

  handleCopyUrl(alias: string) {
    const url = `${window.location.origin}/app/${alias}`;
    navigator.clipboard.writeText(url);
    toast.show('Protocol URL copied to clipboard.', 'success');
    tma.hapticFeedback('medium');
  }

  renderMaintenance() {
    if (!this.root) return;
    this.root.innerHTML = `
      <div class="absolute inset-0 p-8 flex flex-col items-center justify-center space-y-8 bg-slate-900 z-[100] text-white">
        <div class="w-24 h-24 rounded-[2.5rem] bg-amber-500 flex items-center justify-center text-white shadow-2xl shadow-amber-500/30">
           <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.77 3.77z"/></svg>
        </div>
        <div class="text-center space-y-4">
           <h2 class="text-3xl font-black italic uppercase tracking-tighter">System Offline</h2>
           <p class="text-sm text-slate-400 font-mono uppercase tracking-widest max-w-xs mx-auto">This project is currently offline for maintenance.</p>
        </div>
        <button onclick="window.nexus.handleBackToDashboard()" class="px-10 py-5 bg-white text-slate-900 rounded-[2.5rem] text-[10px] font-black uppercase tracking-widest shadow-2xl">Back to Marketplace</button>
      </div>
    `;
  }

  async handlePreviewTemplate(id: string) {
    toast.show('Initializing ephemeral preview...', 'info');
    tma.hapticFeedback('light');
    
    // Mock config for preview
    this.config = {
      name: 'Preview Mode',
      template_id: id,
      currency_name: 'CREDITS',
      status: 'active',
      theme: { accentColor: '#6366f1', primaryColor: '#ffffff', gradient: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)' }
    };
    
    this.currentAlias = 'preview_' + id;
    this.activeTab = 'home';
    this.renderAppShell();
    
    // Add a back to factory button overlay
    const overlay = document.createElement('div');
    overlay.className = 'fixed top-4 left-4 z-[200] animate-in fade-in slide-in-from-top-2 duration-500';
    overlay.innerHTML = `
      <button onclick="window.location.reload()" class="px-6 py-3 bg-slate-900 text-white rounded-2xl text-[9px] font-black uppercase tracking-widest flex items-center gap-2 shadow-2xl">
         <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="m15 18-6-6 6-6"/></svg>
         Exit Preview
      </button>
    `;
    document.body.appendChild(overlay);
  }

  applyThemePreset(bg: string, primary: string, secondary: string, accent: string, icon: string) {
    const bgEl = document.getElementById('f-bg') as HTMLInputElement;
    const color1El = document.getElementById('f-color-1') as HTMLInputElement;
    const color2El = document.getElementById('f-color-2') as HTMLInputElement;
    const color3El = document.getElementById('f-color-3') as HTMLInputElement;
    const iconEl = document.getElementById('f-project-icon') as HTMLSelectElement;

    if (bgEl) bgEl.value = bg;
    if (color1El) color1El.value = primary;
    if (color2El) color2El.value = secondary;
    if (color3El) color3El.value = accent;
    if (iconEl) iconEl.value = icon;

    this.handleFactoryInput('bg', bg);
    this.handleFactoryInput('primary', primary);
    this.handleFactoryInput('secondary', secondary);
    this.handleFactoryInput('accent', accent);
    this.updateLivePreview();
    toast.show('Applied theme preset!', 'success');
  }

  async handleEditProject(id: string) {
    try {
      const data = await db.getAppConfigById(id) as any;
      if (data) {
        (window as any).editingAppId = id;
        (window as any).selectedTemplate = data.template_id;
        window.history.pushState({}, '', '/factory');
        this.init();
        
        // Wait for render to populate fields
        setTimeout(() => {
          (document.getElementById('f-name') as HTMLInputElement).value = data.name;
          (document.getElementById('f-symbol') as HTMLInputElement).value = data.currency_name;
          (document.getElementById('f-bonus') as HTMLInputElement).value = data.welcome_bonus || 0;
          (document.getElementById('f-ref') as HTMLInputElement).value = data.referral_reward || 0;
          (document.getElementById('f-channels') as HTMLInputElement).value = (data.force_join_channels || []).join(', ');
          
          if (data.theme) {
            (document.getElementById('f-color-1') as HTMLInputElement).value = data.theme.primaryColor || '#6366f1';
            (document.getElementById('f-color-2') as HTMLInputElement).value = data.theme.secondaryColor || '#a855f7';
            (document.getElementById('f-color-3') as HTMLInputElement).value = data.theme.accentColor || '#f43f5e';
            (document.getElementById('f-bg') as HTMLInputElement).value = data.theme.bgColor || '#f8fafc';
            (document.getElementById('f-font') as HTMLSelectElement).value = data.theme.fontFamily || 'Outfit';
            (document.getElementById('f-load-grad') as HTMLInputElement).value = data.theme.loadingGradient || '';
          }
          
          if (document.getElementById('f-loading-text')) {
            (document.getElementById('f-loading-text') as HTMLInputElement).value = data.loading_config?.text || 'neural synchronization in progress';
          }
          
          const iconEl = document.getElementById('f-project-icon') as HTMLSelectElement;
          if (iconEl) {
             iconEl.value = data.icon || 'zap';
          }
          
          if (data.bot_config) {
            (document.getElementById('f-bot-token') as HTMLInputElement).value = data.bot_config.bot_token || '';
            (document.getElementById('f-proof-channel') as HTMLInputElement).value = data.bot_config.task_channel_id || '';
            (document.getElementById('f-withdraw-channel') as HTMLInputElement).value = data.bot_config.withdrawal_channel_id || '';
          }

          if (document.getElementById('f-referral-base-url')) {
            (document.getElementById('f-referral-base-url') as HTMLInputElement).value = data.referral_base_url || '';
          }

          (document.getElementById('f-conv-enabled') as HTMLInputElement).checked = !!data.conversion_enabled;
          (document.getElementById('f-conv-rate') as HTMLInputElement).value = (data.conversion_rate || 0.001).toString();
          (document.getElementById('f-conv-currency') as HTMLInputElement).value = data.conversion_currency || 'USDT';
          const minAmount = data.withdrawal_config?.min_amount ?? data.min_withdrawal ?? 500;
          const minTasks = data.withdrawal_config?.min_tasks ?? data.mandatory_tasks_to_withdraw ?? 0;
          (document.getElementById('f-min-withdraw') as HTMLInputElement).value = minAmount.toString();
          (document.getElementById('f-mandatory-tasks') as HTMLInputElement).value = minTasks.toString();

          // Populate Toggles
          if (data.tabs_config) {
             data.tabs_config.forEach((tc: any) => {
                const el = document.getElementById(`tab-toggle-${tc.id}`) as HTMLInputElement;
                const labelEl = document.getElementById(`tab-label-${tc.id}`) as HTMLInputElement;
                if (el) el.checked = tc.enabled !== false;
                if (labelEl) labelEl.value = tc.label || '';
             });
          }
          if (document.getElementById('f-template')) {
             (document.getElementById('f-template') as HTMLSelectElement).value = data.template_id || 'earnhub';
          }
        }, 500);
      }
    } catch (e) {
      toast.show('Failed to load vessel config.', 'error');
    }
  }

  async handlePauseProject(id: string, currentStatus: string) {
    const newStatus = currentStatus === 'paused' ? 'active' : 'paused';
    try {
      await db.updateApp(id, { status: newStatus });
      toast.show(`Project ${newStatus === 'paused' ? 'Paused' : 'Resumed'}`, 'success');
      this.renderDashboard();
    } catch (e) {
      toast.show('Synchronization failure.', 'error');
    }
  }

  async handleDeleteProject(id: string) {
    if (!confirm('Permanent decommissioning requested. Proceed?')) return;
    try {
      await db.deleteApp(id);
      toast.show('Project Deleted.', 'success');
      this.renderDashboard();
    } catch (e) {
      toast.show('Decommissioning failed.', 'error');
    }
  }

  async handleForge() {
    const nameInput = document.getElementById('f-name') as HTMLInputElement;
    const symbolInput = document.getElementById('f-symbol') as HTMLInputElement;
    
    const name = nameInput?.value;
    const symbol = symbolInput?.value;
    const template = (window as any).selectedTemplate || 'hybrid_rewards_x';
    
    if (!name || !symbol) {
      toast.show('Please fill in the required project info.', 'error');
      return;
    }

    const botToken = (document.getElementById('f-bot-token') as HTMLInputElement)?.value;
    const proofChannel = (document.getElementById('f-proof-channel') as HTMLInputElement)?.value;
    const withdrawChannel = (document.getElementById('f-withdraw-channel') as HTMLInputElement)?.value;

    if (!botToken || !proofChannel || !withdrawChannel) {
      toast.show('Missing Telegram credentials (Bot Token or Channel IDs). Please fill them out to complete deployment.', 'warning');
      return;
    }

    // Log the final configuration if requested
    console.log('Finalizing Forge Protocol:', { name, symbol, template });

    tma.hapticFeedback('heavy');
    const loadingToast = toast.show('Initiating deployment sequence...', 'info');
    
    try {
      const isEditing = (window as any).editingAppId;
      const alias = isEditing ? null : name.toLowerCase().replace(/\s+/g, '_').substring(0, 20) + '_' + Math.random().toString(36).substring(2, 5);
      
      const appData: any = {
        name,
        currency_name: symbol,
        template_id: template,
        icon: (document.getElementById('f-project-icon') as HTMLSelectElement)?.value || 'zap',
        referral_base_url: (document.getElementById('f-referral-base-url') as HTMLInputElement)?.value || '',
        welcome_bonus: parseFloat((document.getElementById('f-bonus') as HTMLInputElement)?.value) || 0,
        referral_reward: parseFloat((document.getElementById('f-ref') as HTMLInputElement)?.value) || 0,
        force_join_channels: ((document.getElementById('f-channels') as HTMLInputElement)?.value || '').split(',').map(c => c.trim()).filter(c => c),
        tabs_config: ['home', 'tasks', 'spin', 'refer', 'stats', 'wallet', 'profile', 'logs'].map(id => {
           const label = (document.getElementById(`tab-label-${id}`) as HTMLInputElement)?.value || '';
           return { 
             id, 
             enabled: (document.getElementById(`tab-toggle-${id}`) as HTMLInputElement)?.checked || false,
             label: label,
             icon: '' 
           };
        }),
        conversion_enabled: (document.getElementById('f-conv-enabled') as HTMLInputElement)?.checked || false,
        conversion_rate: parseFloat((document.getElementById('f-conv-rate') as HTMLInputElement)?.value) || 0.001,
        conversion_currency: (document.getElementById('f-conv-currency') as HTMLInputElement)?.value || 'USDT',
        min_withdrawal: parseFloat((document.getElementById('f-min-withdraw') as HTMLInputElement)?.value) || 500,
        mandatory_tasks_to_withdraw: parseInt((document.getElementById('f-mandatory-tasks') as HTMLInputElement)?.value) || 0,
        withdrawal_config: {
          min_amount: parseFloat((document.getElementById('f-min-withdraw') as HTMLInputElement)?.value) || 500,
          min_tasks: parseInt((document.getElementById('f-mandatory-tasks') as HTMLInputElement)?.value) || 0,
          modes: ['crypto', 'bank'],
          restricted: false
        },
        loading_config: {
          text: (document.getElementById('f-loading-text') as HTMLInputElement)?.value || 'neural synchronization in progress',
          color: (document.getElementById('f-color-1') as HTMLInputElement)?.value || '#6366f1'
        },
        theme: {
          primaryColor: (document.getElementById('f-color-1') as HTMLInputElement)?.value || '#6366f1',
          secondaryColor: (document.getElementById('f-color-2') as HTMLInputElement)?.value || '#a855f7',
          accentColor: (document.getElementById('f-color-3') as HTMLInputElement)?.value || '#f43f5e',
          bgColor: (document.getElementById('f-bg') as HTMLInputElement)?.value || '#f8fafc',
          fontFamily: (document.getElementById('f-font') as HTMLSelectElement)?.value || 'Outfit',
          loadingGradient: (document.getElementById('f-load-grad') as HTMLInputElement)?.value || '',
          cardColor: 'rgba(255, 255, 255, 0.8)'
        },
        bot_config: {
          bot_token: (document.getElementById('f-bot-token') as HTMLInputElement)?.value || '',
          task_channel_id: (document.getElementById('f-proof-channel') as HTMLInputElement)?.value || '',
          withdrawal_channel_id: (document.getElementById('f-withdraw-channel') as HTMLInputElement)?.value || ''
        }
      };

      if (!isEditing) {
        if (alias) appData.alias = alias;
        appData.owner_id = this.user.id.toString();
        const newApp = await db.createApp(appData);
        const url = `${window.location.origin}/?alias=${newApp.alias}`;
        
        this.root!.innerHTML = `
          <div class="absolute inset-0 z-50 flex items-center justify-center p-4 glass">
            <div class="premium-card max-w-[92%] w-full p-8 text-center space-y-6 flex flex-col items-center">
              <div class="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center text-green-500 mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              </div>
              <h2 class="text-2xl font-black uppercase italic tracking-tighter text-slate-800">Deployment Successful</h2>
              <p class="text-xs text-slate-500">Your App is now live! Share this link with your users.</p>
              
              <div class="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl break-all font-mono text-[10px] text-slate-400 select-all">
                ${url}
              </div>
              
              <div class="grid grid-cols-1 w-full gap-3">
                <button onclick="navigator.clipboard.writeText('${url}').then(() => { this.innerText = 'COPIED!'; setTimeout(() => this.innerText = 'COPY LINK', 2000); })" class="btn-primary py-4 w-full">COPY LINK</button>
                <button onclick="window.nexus.handleBackToDashboard()" class="py-4 w-full text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-800 transition-colors">Return to Console</button>
              </div>
            </div>
          </div>
        `;
        toast.show('Project deployed to Nexus network!', 'success');
        return; // Don't redirect immediately to show the URL
      } else {
        await db.updateApp(isEditing, appData);
        toast.show('Project settings updated.', 'success');
        (window as any).editingAppId = null;
      }
      
      setTimeout(() => this.handleBackToDashboard(), 1000);
    } catch (error) {
      console.error('Forge Error:', error);
      toast.show('Deployment failed. Connection error.', 'error');
    }
  }

  private changeLog: string[] = [];
  
  handleFactoryInput(property: string, value: any) {
    if (!this.changeLog.includes(property)) {
       this.changeLog.push(property);
       const logEl = document.getElementById('factory-change-log');
       if (logEl) {
          const item = document.createElement('div');
          item.className = 'text-[7px] font-black uppercase tracking-widest text-primary animate-in slide-in-from-left-2 duration-300 flex items-center gap-2';
          item.innerHTML = `
            <span class="w-1 h-1 bg-primary rounded-full"></span>
            Modified: ${property.replace('-', ' ')}
            <span class="text-[6px] opacity-40 lowercase font-mono">/src/main.ts</span>
          `;
          logEl.appendChild(item);
          setTimeout(() => item.classList.add('opacity-40'), 3000);
          logEl.scrollTop = logEl.scrollHeight;
       }
    }
    
    this.updateLivePreview();
  }

  updateLivePreview() {
     const previewContainer = document.getElementById('factory-preview-frame');
     if (!previewContainer) return;
     
     const config = {
        name: (document.getElementById('f-name') as HTMLInputElement)?.value || 'My App',
        currency_name: (document.getElementById('f-symbol') as HTMLInputElement)?.value || 'PTS',
        template_id: (window as any).selectedTemplate || 'hybrid_rewards_x',
        theme: {
           primaryColor: (document.getElementById('f-color-1') as HTMLInputElement)?.value,
           secondaryColor: (document.getElementById('f-color-2') as HTMLInputElement)?.value,
           accentColor: (document.getElementById('f-color-3') as HTMLInputElement)?.value,
           bgColor: (document.getElementById('f-bg') as HTMLInputElement)?.value,
           fontFamily: (document.getElementById('f-font') as HTMLSelectElement)?.value,
        }
     };

     // Render an ephemeral version in the preview frame
     this.renderPreviewInElement(previewContainer, config);
  }

  async renderPreviewInElement(el: HTMLElement, config: any) {
     const templateId = config.template_id;
     try {
        const module = await loadTemplate(templateId);
        const template = module.default;
        
        // Apply theme to the container locally
        const root = el;
        
        // Reuse same logic as applyTheme for consistency
        const hexToRgb = (hex: string) => {
          const res = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
          return res ? { r: parseInt(res[1], 16), g: parseInt(res[2], 16), b: parseInt(res[3], 16) } : { r: 255, g: 255, b: 255 };
        };
        const getLuminance = (hex: string) => {
          const rgb = hexToRgb(hex);
          return (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
        };
        
        const bgLuminance = getLuminance(config.theme.bgColor || '#f8fafc');
        const isDarkBg = bgLuminance < 0.5;
        const textPrimary = isDarkBg ? '#ffffff' : '#0f172a';
        
        root.style.setProperty('--primary-color', config.theme.primaryColor || '#6366f1');
        root.style.setProperty('--secondary-color', config.theme.secondaryColor || '#a855f7');
        root.style.setProperty('--accent-color', config.theme.accentColor || '#f43f5e');
        root.style.setProperty('--bg-color', config.theme.bgColor || '#f8fafc');
        root.style.setProperty('--text-primary', textPrimary);
        root.style.setProperty('--text-secondary', isDarkBg ? 'rgba(255,255,255,0.7)' : '#64748b');
        root.style.setProperty('--card-bg', isDarkBg ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 0.8)');
        root.style.setProperty('--card-border', isDarkBg ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)');
        root.style.setProperty('--navbar-bg', isDarkBg ? 'rgba(15, 23, 42, 0.8)' : 'rgba(255, 255, 255, 0.8)');
        root.style.setProperty('--primary-btn-bg', `linear-gradient(135deg, ${config.theme.primaryColor} 0%, ${config.theme.secondaryColor} 100%)`);
        root.style.setProperty('--primary-btn-text', getLuminance(config.theme.primaryColor) < 0.6 ? '#ffffff' : '#0f172a');
        root.style.setProperty('--secondary-btn-bg', isDarkBg ? 'rgba(255,255,255,0.1)' : '#f1f5f9');
        root.style.setProperty('--secondary-btn-text', isDarkBg ? '#ffffff' : '#0f172a');
        
        root.style.fontFamily = `'${config.theme.fontFamily || 'Outfit'}', sans-serif`;
        root.style.backgroundColor = config.theme.bgColor || '#f8fafc';
        root.style.color = textPrimary;

        // Clear and render specific view (home by default for preview)
        el.innerHTML = '';
        await template(el, config, this.user, 'home'); 

     } catch (e) { console.error('Preview error', e); }
  }

  renderFactory() {
      if (!this.root) return;
      const isEditing = !!(window as any).editingAppId;
      this.changeLog = [];
      
      this.root.innerHTML = `
        <div class="absolute inset-0 pointer-events-none opacity-40 overflow-hidden bg-white">
           <div class="absolute top-1/4 right-1/4 w-[500px] h-[500px] bg-indigo-500/5 rounded-full blur-[120px]"></div>
        </div>

        <div class="flex flex-col md:flex-row h-full absolute inset-0 overflow-y-auto">
           <!-- LEFT: FORM -->
           <div class="w-full md:w-[450px] bg-white border-r border-slate-100 flex flex-col h-full z-20 overflow-y-auto">
              <header class="p-6 border-b border-slate-50 flex items-center justify-between">
                 <div class="flex items-center gap-4">
                    <button onclick="window.nexus.handleBackToDashboard()" class="w-10 h-10 glass rounded-2xl flex items-center justify-center active:scale-90 transition-all border border-slate-100 text-slate-800">
                       <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
                    </button>
                    <div>
                       <h2 class="text-lg font-black italic uppercase tracking-tight text-slate-800">Forge Interface</h2>
                       <p class="text-[7px] font-black uppercase tracking-[0.4em] text-indigo-500">Node Configuration</p>
                    </div>
                 </div>
                 <div id="factory-change-log" class="flex flex-col items-end gap-1"></div>
              </header>

              <div class="flex-1 overflow-y-auto p-6 space-y-8 pb-32 bg-inherit">
                 <!-- Form Content -->
                 <div class="space-y-6">
                    <div class="space-y-4">
                       <p class="text-[9px] font-black uppercase tracking-[0.3em] text-slate-400 border-b border-slate-50 pb-2">01 Basic Info</p>
                       <div class="space-y-4">
                          <div class="space-y-2">
                              <label class="text-[9px] font-black uppercase tracking-widest text-slate-500 block px-1">Display Name</label>
                              <input id="f-name" oninput="window.nexus.handleFactoryInput('name', this.value)" type="text" placeholder="e.g. My Reward App" class="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-slate-800 outline-none focus:border-indigo-500 transition-all font-mono text-xs">
                          </div>
                          <div class="space-y-2">
                              <label class="text-[9px] font-black uppercase tracking-widest text-slate-500 block px-1">Unit Name</label>
                              <input id="f-symbol" oninput="window.nexus.handleFactoryInput('symbol', this.value)" type="text" placeholder="e.g. POINTS" class="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-slate-800 outline-none focus:border-indigo-500 transition-all font-mono text-xs">
                           </div>
                           <div class="space-y-2">
                               <label class="text-[9px] font-black uppercase tracking-widest text-slate-500 block px-1">Layout Preset / Template Style</label>
                               <select id="f-template" onchange="(window).selectedTemplate = this.value; window.nexus.updateLivePreview();" class="w-full h-12 bg-slate-50 border border-slate-100 rounded-2xl px-4 text-xs font-bold text-slate-800 outline-none transition-all focus:border-indigo-500">
                                  <option value="earnhub">EarnHub (Complete Multi-task Engagement Hub)</option>
                                  <option value="sparkbnb">Sparkbnb (Premium Real Estate Rental Hub)</option>
                                  <option value="duxbux">DuxBux (Interactive Cashier Task System)</option>
                               </select>
                          </div>
                       </div>
                    </div>

                    <div class="space-y-4">
                       <p class="text-[9px] font-black uppercase tracking-[0.3em] text-slate-400 border-b border-slate-50 pb-2">02 Theme & Style</p>
                       
                       <div class="space-y-2">
                          <label class="text-[9px] font-black uppercase tracking-widest text-slate-500 block px-1 font-bold">Theme Presets (Quick Setup)</label>
                          <div class="grid grid-cols-2 gap-2 pb-2">
                             <button type="button" onclick="window.nexus.applyThemePreset('#0c0f16', '#c0ff4d', '#161b26', '#c0ff4d', 'flame')" class="p-3 bg-[#0c0f16] border border-white/10 rounded-2xl text-left hover:border-indigo-400 transition-colors">
                                <p class="text-[9px] font-black text-white italic uppercase leading-none">EarnHub Dark</p>
                                <span class="text-[7px] text-[#c0ff4d] font-mono leading-none">Lime & Deep Obsidian</span>
                             </button>
                             <button type="button" onclick="window.nexus.applyThemePreset('#0a0a14', '#39ff14', '#131326', '#ff007f', 'zap')" class="p-3 bg-[#0a0a14] border border-white/10 rounded-2xl text-left hover:border-indigo-400 transition-colors">
                                <p class="text-[9px] font-black text-white italic uppercase leading-none">Cyber Neon</p>
                                <span class="text-[7px] text-[#39ff14] font-mono leading-none">Green & Neon Magenta</span>
                             </button>
                             <button type="button" onclick="window.nexus.applyThemePreset('#120a02', '#ffd700', '#2c1c09', '#ff8c00', 'crown')" class="p-3 bg-[#120a02] border border-white/10 rounded-2xl text-left hover:border-indigo-400 transition-colors">
                                <p class="text-[9px] font-black text-white italic uppercase leading-none">Gold Crown</p>
                                <span class="text-[7px] text-[#ffd700] font-mono leading-none">Imperial Gold Onyx</span>
                             </button>
                             <button type="button" onclick="window.nexus.applyThemePreset('#02120a', '#00ff88', '#082c18', '#00e5ff', 'shield')" class="p-3 bg-[#02120a] border border-white/10 rounded-2xl text-left hover:border-indigo-400 transition-colors">
                                <p class="text-[9px] font-black text-white italic uppercase leading-none">Emerald Rift</p>
                                <span class="text-[7px] text-[#00ff88] font-mono leading-none">Green & Mint Cyan</span>
                             </button>
                             <button type="button" onclick="window.nexus.applyThemePreset('#1a0b12', '#ff5e62', '#3a1324', '#ff9966', 'star')" class="p-3 bg-[#1a0b12] border border-white/10 rounded-2xl text-left hover:border-indigo-400 transition-colors">
                                <p class="text-[9px] font-black text-white italic uppercase leading-none">Sunset Mirage</p>
                                <span class="text-[7px] text-[#ff5e62] font-mono leading-none">Sunset Warm Orange</span>
                             </button>
                             <button type="button" onclick="window.nexus.applyThemePreset('#0f172a', '#6366f1', '#1e293b', '#f43f5e', 'target')" class="p-3 bg-[#0f172a] border border-slate-800 rounded-2xl text-left hover:border-indigo-400 transition-colors">
                                <p class="text-[9px] font-black text-white italic uppercase leading-none">Indigo Aura</p>
                                <span class="text-[7px] text-[#6366f1] font-mono leading-none">Standard Classic Blue</span>
                              </button>
                          </div>
                       </div>

                       <div class="space-y-2">
                          <label class="text-[9px] font-black uppercase tracking-widest text-slate-500 block px-1">Launcher Icon (App Icon)</label>
                          <select id="f-project-icon" class="w-full h-12 bg-slate-50 border border-slate-100 rounded-xl px-4 text-xs font-bold text-slate-800 outline-none transition-all focus:border-indigo-500 font-bold">
                             <option value="zap">⚡ Lightning (Zap)</option>
                             <option value="flame">🔥 Fire (Flame)</option>
                             <option value="star">⭐ Star (Star)</option>
                             <option value="crown">👑 Crown (Crown)</option>
                             <option value="shield">🛡️ Guard (Shield)</option>
                             <option value="target">🎯 Goal (Target)</option>
                             <option value="diamond">💎 Gem (Diamond)</option>
                             <option value="hexagon">⬢ Hexagon (Hexagon)</option>
                             <option value="rocket">🚀 Rocket (Rocket)</option>
                             <option value="ghost">👻 Ghost (Ghost)</option>
                             <option value="fingerprint">👣 Security (Fingerprint)</option>
                             <option value="cpu">💻 Chip (Cpu)</option>
                          </select>
                       </div>

                       <div class="grid grid-cols-2 gap-4 col-span-2">
                          <div class="space-y-2">
                             <label class="text-[9px] font-black uppercase text-slate-500">App Background</label>
                             <input id="f-bg" oninput="window.nexus.handleFactoryInput('bg', this.value)" type="color" value="#f8fafc" class="w-full h-12 bg-white border border-slate-100 rounded-xl p-1 cursor-pointer">
                          </div>
                          <div class="space-y-2">
                             <label class="text-[9px] font-black uppercase text-slate-500">Primary Color</label>
                             <input id="f-color-1" oninput="window.nexus.handleFactoryInput('primary', this.value)" type="color" value="#6366f1" class="w-full h-12 bg-white border border-slate-100 rounded-xl p-1 cursor-pointer">
                          </div>
                          <div class="space-y-2">
                             <label class="text-[9px] font-black uppercase text-slate-500">Secondary</label>
                             <input id="f-color-2" oninput="window.nexus.handleFactoryInput('secondary', this.value)" type="color" value="#a855f7" class="w-full h-12 bg-white border border-slate-100 rounded-xl p-1 cursor-pointer">
                          </div>
                          <div class="space-y-2">
                             <label class="text-[9px] font-black uppercase text-slate-500">Accent</label>
                             <input id="f-color-3" oninput="window.nexus.handleFactoryInput('accent', this.value)" type="color" value="#f43f5e" class="w-full h-12 bg-white border border-slate-100 rounded-xl p-1 cursor-pointer">
                          </div>
                       </div>
                       <div class="space-y-2">
                          <label class="text-[9px] font-black uppercase tracking-widest text-slate-500 block px-1">Typography</label>
                          <select id="f-font" onchange="window.nexus.handleFactoryInput('font', this.value)" class="w-full h-12 bg-slate-50 border border-slate-100 rounded-xl px-4 text-xs font-bold text-slate-800 outline-none transition-all focus:border-indigo-500">
                             <option value="Outfit">Outfit (Modern)</option>
                             <option value="Inter">Inter (Clean)</option>
                             <option value="Playfair Display">Playfair (Luxury)</option>
                             <option value="Space Grotesk">Space (Tech)</option>
                             <option value="JetBrains Mono">JetBrains (Code)</option>
                          </select>
                       </div>
                    </div>

                    <div class="space-y-4">
                       <p class="text-[9px] font-black uppercase tracking-[0.3em] text-slate-400 border-b border-slate-50 pb-2">03 Connection & Economy</p>
                       <div class="space-y-4">
                          <div class="grid grid-cols-2 gap-4">
                             <div class="space-y-2">
                                <label class="text-[9px] font-black uppercase text-slate-500">Sign-up Bonus</label>
                                <input id="f-bonus" oninput="window.nexus.handleFactoryInput('bonus', this.value)" type="number" value="100" class="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-xs font-mono outline-none">
                             </div>
                             <div class="space-y-2">
                                <label class="text-[9px] font-black uppercase text-slate-500">Invite Reward</label>
                                <input id="f-ref" oninput="window.nexus.handleFactoryInput('referral', this.value)" type="number" value="50" class="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-xs font-mono outline-none">
                             </div>
                          </div>
                          <div class="space-y-2">
                             <label class="text-[9px] font-black uppercase text-slate-500">Bot Token (BotFather)</label>
                             <input id="f-bot-token" type="password" placeholder="12345:..." class="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-xs font-mono outline-none">
                           </div>
                           <div class="space-y-2">
                              <label class="text-[9px] font-black uppercase text-slate-500">Custom Referral Link Base (Avoids Leaking Console URL)</label>
                              <input id="f-referral-base-url" type="text" placeholder="https://t.me/YourTelegramBot/app" class="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-xs font-mono outline-none">
                              <p class="text-[7px] text-slate-400 font-mono uppercase tracking-wider mt-1 px-1">Example: https://t.me/your_telegram_bot/app. When users copy invite links, they will receive this URL with their referral ID attached.</p>
                          </div>
                          <div class="grid grid-cols-2 gap-4">
                             <div class="space-y-2">
                                <label class="text-[9px] font-black uppercase text-slate-500">Proof Channel ID</label>
                                <input id="f-proof-channel" type="text" placeholder="-100..." class="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-xs font-mono outline-none">
                             </div>
                             <div class="space-y-2">
                                <label class="text-[9px] font-black uppercase text-slate-500">Withdraw Alerts ID</label>
                                <input id="f-withdraw-channel" type="text" placeholder="-100..." class="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-xs font-mono outline-none">
                             </div>
                          </div>
                          <div class="space-y-2">
                             <label class="text-[9px] font-black uppercase text-slate-500">Mandatory Subscriptions</label>
                             <input id="f-channels" type="text" placeholder="@Channel, @Group" class="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-xs font-mono outline-none">
                          </div>
                       </div>
                    </div>

                    <div class="space-y-4">
                       <p class="text-[9px] font-black uppercase tracking-[0.3em] text-slate-400 border-b border-slate-50 pb-2">04 Conversion & Withdrawal</p>
                       <div class="p-4 bg-indigo-50/30 rounded-2xl border border-indigo-100/50 space-y-4">
                          <div class="flex items-center justify-between">
                             <p class="text-[9px] font-black uppercase text-indigo-600">Enable Payouts</p>
                             <input type="checkbox" id="f-conv-enabled" class="w-4 h-4 accent-indigo-600">
                          </div>
                          <div class="grid grid-cols-2 gap-4">
                             <div class="space-y-2">
                                <label class="text-[8px] font-black uppercase text-slate-500">Rate (1 Unit = ?)</label>
                                <input id="f-conv-rate" type="number" step="0.000001" value="0.001" class="w-full bg-white border border-slate-100 rounded-xl p-3 text-xs font-mono outline-none">
                             </div>
                             <div class="space-y-2">
                                <label class="text-[8px] font-black uppercase text-slate-500">Target Currency</label>
                                <input id="f-conv-currency" type="text" value="USDT" class="w-full bg-white border border-slate-100 rounded-xl p-3 text-xs font-mono uppercase outline-none">
                             </div>
                          </div>
                       </div>
                       <div class="grid grid-cols-2 gap-4">
                          <div class="space-y-2">
                             <label class="text-[9px] font-black uppercase text-slate-500">Min. Payout</label>
                             <input id="f-min-withdraw" type="number" value="500" class="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-xs font-mono outline-none">
                          </div>
                          <div class="space-y-2">
                             <label class="text-[9px] font-black uppercase text-slate-500">Mandatory Tasks</label>
                             <input id="f-mandatory-tasks" type="number" value="0" class="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-xs font-mono outline-none">
                          </div>
                       </div>
                    </div>

                    <div class="space-y-4">
                       <p class="text-[9px] font-black uppercase tracking-[0.3em] text-slate-400 border-b border-slate-50 pb-2">05 Navigation Nodes</p>
                       <div class="space-y-3">
                          ${['home', 'tasks', 'spin', 'refer', 'wallet', 'profile', 'logs', 'analytics'].map(tab => {
                             const isMandatory = ['home', 'refer', 'profile', 'wallet'].includes(tab);
                             return `
                             <div class="flex items-center justify-between p-3 bg-slate-50 rounded-2xl border border-slate-100">
                                <div class="flex items-center gap-3">
                                   <input type="checkbox" id="tab-toggle-${tab}" ${isMandatory ? 'checked disabled' : 'checked'} class="w-4 h-4 accent-indigo-600">
                                   <p class="text-[9px] font-black uppercase tracking-widest text-slate-800">${tab}</p>
                                </div>
                                <input id="tab-label-${tab}" type="text" placeholder="Label" class="bg-white px-3 py-1.5 rounded-xl border border-slate-100 text-[8px] font-mono outline-none w-20">
                             </div>
                          `}).join('')}
                       </div>
                    </div>

                    <div class="space-y-2">
                       <label class="text-[9px] font-black uppercase text-slate-500 px-1">Loading Screen Text</label>
                       <input id="f-loading-text" type="text" placeholder="neural synchronization in progress" class="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-xs outline-none">
                    </div>

                    <div class="space-y-2">
                       <label class="text-[9px] font-black uppercase text-slate-500 px-1">Loading Screen CSS</label>
                       <input id="f-load-grad" type="text" placeholder="linear-gradient(...)" class="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-[8px] font-mono outline-none">
                    </div>
                 </div>
              </div>

              <div class="p-6 bg-slate-50/50 backdrop-blur-md border-t border-slate-100">
                 <button onclick="window.nexus.handleForge()" class="btn-primary w-full py-6 text-[10px] shadow-indigo-100 shadow-2xl">
                    ${isEditing ? 'UPDATE VESSEL' : 'DECOUPLE & DEPLOY'}
                 </button>
              </div>
           </div>

           <!-- RIGHT: LIVE PREVIEW -->
           <div class="hidden md:flex flex-1 bg-slate-50 items-center justify-center p-12 overflow-hidden relative">
              <div class="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]"></div>
              
              <div class="relative w-[375px] h-[750px] bg-white rounded-[3.5rem] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.3)] border-[8px] border-slate-900 overflow-hidden flex flex-col">
                 <div class="h-6 w-full bg-slate-900 flex items-center justify-center">
                    <div class="w-20 h-4 bg-slate-800 rounded-full"></div>
                 </div>
                 <div id="factory-preview-frame" class="flex-1 overflow-y-auto bg-slate-50 relative">
                    <!-- Preview content injected here -->
                    <div class="flex flex-col items-center justify-center h-full gap-4 opacity-20">
                       <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                       <p class="text-[10px] font-black uppercase tracking-widest">Awaiting Preview</p>
                    </div>
                 </div>
                 <div class="h-12 w-full bg-white border-t border-slate-50 flex items-center justify-center px-6 gap-8">
                    <div class="w-2 h-2 rounded-full bg-slate-200"></div>
                    <div class="w-2 h-2 rounded-full bg-slate-200"></div>
                    <div class="w-2 h-2 rounded-full bg-indigo-500"></div>
                    <div class="w-2 h-2 rounded-full bg-slate-200"></div>
                 </div>
              </div>
              
              <!-- Floating Preview Controls -->
              <div class="absolute bottom-8 right-8 flex gap-2">
                 <div class="px-4 py-2 bg-slate-900 text-white rounded-xl text-[8px] font-black uppercase tracking-widest shadow-2xl opacity-60">Live Mode</div>
              </div>
           </div>
           
           <!-- MOBILE PREVIEW TOGGLE (Floating) -->
           <button class="md:hidden fixed bottom-32 right-6 w-14 h-14 bg-indigo-600 rounded-full shadow-2xl z-[100] flex items-center justify-center text-white active:scale-90 transition-all">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><rect width="14" height="20" x="5" y="2" rx="2" ry="2"/><path d="M12 18h.01"/></svg>
           </button>
        </div>
      `;
      
      // Initial Preview Render
      setTimeout(() => this.updateLivePreview(), 500);
   }

   async handleModerateWithdrawal(appId: string, wId: string, status: string) {
     tma.hapticFeedback('medium');
     toast.show(`Force updating payout to ${status}...`, 'info');
     try {
        await db.updateWithdrawalStatus(appId, wId, status as any);
        toast.show('Global Moderate Success', 'success');
        this.renderPlatformContent();
     } catch (e) { toast.show('Moderation failed.', 'error'); }
  }

  handleTemplateSelect(id: string, el: HTMLElement) {
    (window as any).selectedTemplate = id;
    const cards = el.parentElement?.querySelectorAll('.glass');
    cards?.forEach(c => {
      c.classList.remove('border-indigo-500', 'border-2');
      c.classList.add('border-slate-100', 'border');
    });
    el.classList.add('border-indigo-500', 'border-2');
    el.classList.remove('border-slate-100', 'border');
  }

  async handleUpdateSuperAdminId() {
     const lockId = (document.getElementById('super-admin-lock') as HTMLInputElement).value;
     tma.hapticFeedback('medium');
     toast.show('Securing global access...', 'info');
     try {
        await db.setGlobalSetting('super_admin_id', lockId);
        toast.show('Super Admin ID Locked!', 'success');
        this.renderPlatformContent();
     } catch (e) { toast.show('Failed to lock ID.', 'error'); }
  }

  selectedSuperTemplateId: string = 'duxbux';

  handleSuperTemplateDbSelect(templateId: string) {
     tma.hapticFeedback('light');
     this.selectedSuperTemplateId = templateId;
     this.renderPlatformContent();
  }

  async handleSaveSuperTemplateDb(templateId: string) {
     tma.hapticFeedback('medium');
     const apiKey = (document.getElementById('super-db-apikey') as HTMLInputElement).value;
     const projectId = (document.getElementById('super-db-projectid') as HTMLInputElement).value;
     const authDomain = (document.getElementById('super-db-authdomain') as HTMLInputElement).value;
     const storageBucket = (document.getElementById('super-db-bucket') as HTMLInputElement).value;

     toast.show('Updating isolated records...', 'info');
     try {
        await db.saveTemplateCredentials(templateId, {
           apiKey,
           projectId,
           authDomain: authDomain || (projectId ? `${projectId}.firebaseapp.com` : ''),
           storageBucket: storageBucket || (projectId ? `${projectId}.appspot.com` : ''),
        });
        toast.show('Isolated DB updated!', 'success');
        this.renderPlatformContent();
     } catch (e) {
        toast.show('Failed to save isolated settings.', 'error');
     }
  }

  	handleShowBroadcastModal() {
		tma.hapticFeedback('medium');
		const modal = document.createElement('div');
		modal.className = 'absolute inset-0 z-[2000] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-md animate-in fade-in duration-300';
		modal.innerHTML = `
			<div class="premium-card max-w-sm w-[95%] p-6 space-y-5 bg-white shadow-3xl">
				<div class="space-y-1">
					<h3 class="text-xl font-black italic uppercase tracking-tighter text-slate-800">Broadcast Signal</h3>
					<p class="text-[9px] text-slate-400 font-mono uppercase tracking-widest">Global notification dispatch</p>
				</div>
				
				<div class="space-y-4">
					<input id="broad-title" type="text" placeholder="Title (e.g. System Update)" class="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-xs font-black outline-none tracking-tight">
					<textarea id="broad-msg" placeholder="Message content..." class="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-xs font-medium outline-none h-28 resize-none"></textarea>
					
					<div class="space-y-1">
						<label class="text-[8px] font-black uppercase text-slate-400 tracking-wider">Target Partition</label>
						<select id="broad-target" class="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-[10px] font-black uppercase">
							<option value="users">Partition: Mini-App Users (All Profiles)</option>
							<option value="developers">Partition: Developers / Mini-Creators</option>
						</select>
					</div>

					<div class="flex items-center gap-2 bg-slate-50 p-3.5 rounded-xl border border-slate-100">
						<input type="checkbox" id="broad-noticeboard" checked class="w-4 h-4 accent-indigo-600">
						<label for="broad-noticeboard" class="text-[9px] text-slate-500 font-bold uppercase select-none cursor-pointer">Show on Portal Notice Board</label>
					</div>

					<select id="broad-priority" class="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-[10px] font-black uppercase">
						<option value="normal">Priority: Normal</option>
						<option value="high">Priority: Critical</option>
					</select>
					
					<button onclick="window.nexus.handleBroadcastNotification()" class="btn-primary w-full py-5 text-[10px]">DISPATCH SIGNAL</button>
					<button onclick="this.closest('.absolute').remove()" class="w-full text-[9px] font-black uppercase tracking-widest text-slate-400 py-2">Decline</button>
				</div>
			</div>
		 `;
		document.body.appendChild(modal);
	}

	async handleBroadcastNotification() {
		if (!this.isSuperAdmin) {
			toast.show('Access Denied: Root Authority Required', 'error');
			return;
		}
		const title = (document.getElementById('broad-title') as HTMLInputElement).value;
		const message = (document.getElementById('broad-msg') as HTMLTextAreaElement).value;
		const target = (document.getElementById('broad-target') as HTMLSelectElement).value;
		const showOnNoticeBoard = (document.getElementById('broad-noticeboard') as HTMLInputElement).checked;
		const priority = (document.getElementById('broad-priority') as HTMLSelectElement).value;
		
		if (!title || !message) {
			toast.show('Signal content incomplete.', 'warning');
			return;
		}

		tma.hapticFeedback('heavy');
		
		try {
			if (showOnNoticeBoard) {
				toast.show('Broadcasting platform notification...', 'info');
				await db.createNotification({
					title,
					message,
					priority,
					target_user_id: 'all'
				});
				await db.setGlobalSetting('announcement', {
					id: Date.now(),
					text: "[" + title + "] " + message,
					active: true,
					timestamp: new Date()
				}).catch(() => null);
			}

			toast.show("Broadcasting Telegram signals [Target: " + target.toUpperCase() + "]...", 'info');

			const apps: any[] = (await db.getAllApps().catch(() => [])) || [];
			const broadcastText = "📢 <b>" + escapeHTML(title) + "</b>\n\n" + escapeHTML(message);
			
			let successCount = 0;
			let failCount = 0;

			if (target === 'developers') {
				const devBotToken = await db.getGlobalSetting('dev_broadcast_bot_token').catch(() => null);
				if (!devBotToken) {
					toast.show('Developer Bot Token is not configured in settings. Cannot send Telegram notifications.', 'error');
					return;
				}
				
				const developerUserIds = Array.from(new Set(apps.map((app: any) => app.owner_id).filter(id => id && !isNaN(Number(id)))));
				if (developerUserIds.length === 0) {
					toast.show('No active developers detected in the system directory.', 'warning');
				} else {
					for (const developerId of developerUserIds) {
						try {
							const res = await fetch(`https://api.telegram.org/bot${devBotToken}/sendMessage`, {
								method: 'POST',
								headers: { 'Content-Type': 'application/json' },
								body: JSON.stringify({
									chat_id: developerId,
									text: broadcastText,
									parse_mode: 'HTML'
								})
							});
							if (res.ok) {
								successCount++;
							} else {
								failCount++;
								const errJson = await res.json().catch(() => null);
								await db.logSystemError('system', 'dev_broadcast_fail', `Broadcast to Developer ID ${developerId} failed: ${errJson?.description || 'Unknown Response'}`);
							}
						} catch (err: any) {
							failCount++;
							await db.logSystemError('system', 'dev_broadcast_fail', `Network broadcast developer failure to ID ${developerId}: ${err?.message || String(err)}`);
						}
					}
					toast.show(`Developer Telegram Broadcast finished! Success: ${successCount}, Failures: ${failCount}`, 'success');
				}
			} else {
				// Target: GENERAL APP USERS
				const profiles = await db.getAllProfiles().catch(() => []);
				const tokensSet = new Set<string>();
				for (const app of apps) {
					const t1 = app.bot_config?.botToken || app.bot_config?.tgBotToken || app.bot_config?.bot_token || app.botToken || app.tgBotToken || app.bot_token;
					if (t1) tokensSet.add(t1);

					const t2 = app.modules?.earnhub?.tgBotToken || app.modules?.earnhub?.botToken || app.modules?.earnhub?.bot_token ||
								app.modules?.duxbux?.botToken || app.modules?.duxbux?.tgBotToken || app.modules?.duxbux?.bot_token ||
								app.modules?.sparkbnb?.botToken || app.modules?.sparkbnb?.tgBotToken || app.modules?.sparkbnb?.bot_token;
					if (t2) tokensSet.add(t2);
				}

				// Gather from template credentials too for fallback
				for (const tempId of ['earnhub', 'sparkbnb', 'duxbux']) {
					const tempCreds = await db.getTemplateCredentials(tempId).catch(() => null);
					if (tempCreds) {
						const b1 = tempCreds.botToken || tempCreds.tgBotToken || tempCreds.bot_token;
						if (b1) tokensSet.add(b1);
					}
				}

				const uniqueTokens = Array.from(tokensSet).filter(t => t && t.trim().length > 5);

				if (uniqueTokens.length === 0) {
					toast.show('No active Telegram bot tokens detected in project directory configurations.', 'warning');
				} else {
					for (const token of uniqueTokens) {
						for (const profile of profiles) {
							const chatId = profile.id;
							if (!chatId || isNaN(Number(chatId))) continue;

							try {
								const res = await fetch(`https://api.telegram.org/bot${token}/sendMessage`, {
									method: 'POST',
									headers: { 'Content-Type': 'application/json' },
									body: JSON.stringify({
										chat_id: chatId,
										text: broadcastText,
										parse_mode: 'HTML'
									})
								});
								if (res.ok) {
									successCount++;
								} else {
									failCount++;
									const errJson = await res.json().catch(() => null);
									await db.logSystemError('system', 'telegram_broadcast', `Broadcast to User ID ${chatId} failed: ${errJson?.description || 'Unknown Response'}`, { bot_token_mask: token.substring(0, 10) + '...' });
								}
							} catch (err: any) {
								failCount++;
								await db.logSystemError('system', 'telegram_broadcast', `Network broadcast user failure to ID ${chatId}: ${err?.message || String(err)}`);
							}
						}
					}
					toast.show(`Telegram User Broadcast finished! Success: ${successCount}, Failures: ${failCount}`, 'success');
				}
			}

			const modal = document.querySelector('.fixed.z-\\[2000\\]') || document.querySelector('.absolute.z-\\[2000\\]');
			if (modal) modal.remove();
		} catch (e: any) { 
			toast.show('Relay error during broadcast.', 'error'); 
			await db.logSystemError('system', 'global_broadcast', `Platform broadcast failed: ${e?.message || String(e)}`);
		}
	}

  renderLoading() {
    if (!this.root) return;
    this.root.innerHTML = '';
    
    // User Controlled Loading Params
    const appName = this.config?.name || 'Nexus Terminal';
    const theme = this.config?.theme || {};
    const loadingText = this.config?.loading_config?.text || 'neural synchronization in progress';
    const accentColor = theme.primaryColor || this.config?.loading_config?.color || '#4f46e5';
    const icon = this.config?.icon || 'zap';
    const gradient = theme.loadingGradient || `linear-gradient(135deg, ${theme.bgColor || '#0f172a'} 0%, ${theme.secondaryColor || '#020617'} 100%)`;

    const iconPaths: Record<string, string> = {
      zap: 'M13 2L3 14h9l-1 8 10-12h-9l1-8z',
      shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
      target: 'M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z M12 17c2.761 0 5-2.239 5-5s-2.239-5-5-5-5 2.239-5 5 2.239 5 5 5z',
      diamond: 'M6 3h12l4 6-10 13L2 9z',
      crown: 'M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7z',
      hexagon: 'M12 2l10 5.8v11.5L12 22 2 16.3V7.8L12 2z',
      star: 'M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z',
      flame: 'M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z',
      rocket: 'M4.5 16.5c-1.5 1.25-2.5 3.5-2.5 3.5s2.25-1 3.5-2.5M12 5c3.5 3.5 6 8 6 8s-4.5 2.5-8 2.5M19 5c1.5-1.5 2.5-.5 2.5-.5s1 1-.5 2.5c-.5 1.5-3.5 4.5-3.5 4.5s-1-1-2.5-2.5 4.5-3.5 4.5-3.5zM9 15l-3 3M15 9l3-3',
      ghost: 'M9 18V5a3 3 0 0 1 6 0v13H9m3-7V9m0 7v-1',
      fingerprint: 'M2 12a10 10 0 0 1 18-3M5 14a7 7 0 0 1 12-4M8 16a4 4 0 0 1 6-2',
      cpu: 'M20 4H4v16h16V4M9 9h6v6H9V9'
    };
    const pathD = iconPaths[icon.toLowerCase()] || iconPaths.zap;

    this.root.innerHTML = `
      <div class="fixed inset-0 flex items-center justify-center overflow-hidden z-[9999]" style="background: ${gradient}">
        <!-- Cyber grid overlay -->
        <div class="absolute inset-0 opacity-[0.03] pointer-events-none" style="background-image: radial-gradient(${accentColor} 1px, transparent 0); background-size: 30px 30px;"></div>
        
        <div class="relative z-10 flex flex-col items-center justify-center p-8 text-center space-y-12 animate-in fade-in zoom-in duration-1000">
           <!-- Floating Core Component -->
           <div class="relative group animate-float">
              <div class="absolute inset-0 bg-indigo-500/20 rounded-full blur-3xl animate-pulse group-hover:bg-indigo-500/40 transition-all duration-1000"></div>
              <div class="w-28 h-28 bg-slate-900 border border-white/10 rounded-[3rem] flex items-center justify-center shadow-[0_0_50px_rgba(0,0,0,0.5)] relative z-10 shadow-indigo-500/20">
                 <svg id="loading-icon" xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="${accentColor}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="animate-pulse drop-shadow-[0_0_15px_${accentColor}]">
                    <path id="icon-path" d="${pathD}"/>
                 </svg>
              </div>
           </div>

           <div class="space-y-4">
              <h1 class="text-4xl font-black italic uppercase tracking-tighter text-white/90 drop-shadow-2xl animate-in slide-in-from-bottom-4 duration-700">${appName}</h1>
              <div class="flex flex-col items-center gap-5">
                 <p class="text-[9px] font-mono font-black text-indigo-400 uppercase tracking-[0.5em] animate-pulse drop-shadow-[0_0_10px_rgba(99,102,241,0.4)]">${loadingText}</p>
                 <div class="w-56 h-1 w-full max-w-[200px] bg-white/5 rounded-full overflow-hidden relative border border-white/[0.03]">
                    <div class="absolute inset-y-0 left-0 bg-gradient-to-r from-transparent via-indigo-500 to-transparent w-full animate-[loading-bar_2s_infinite_linear]" style="background-color: ${accentColor}"></div>
                 </div>
              </div>
           </div>
        </div>
      </div>
      
      <style>
        @keyframes loading-bar {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      </style>
    `;

    // Dynamic Icon Switcher based on Admin Config
    setTimeout(() => {
        const path = document.getElementById('icon-path');
        if (!path) return;
        const iconPaths: Record<string, string> = {
            shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
            target: 'M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z',
            diamond: 'M6 3h12l4 6-10 12L2 9l4-6z',
            crown: 'M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7z',
            flame: 'M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3 1.11.43 2 1.55 2 3a2.5 2.5 0 0 0 2.5 2.5 2.5 2.5 0 0 0 2.5-2.5c0-2-1.5-4-3-6 1.5 2.5 1.5 4 1.5 4.5a1.5 1.5 0 1 1-3 0c0-1 1-2.5 1-4-2 2-3 4-3 6a2.5 2.5 0 0 0 2.5 2.5z',
            rocket: 'M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09zM15 9l-9 9m13.5-3.5L15 9l-1.5-4.5 9-1 1 9-4.5-1.5z',
            activity: 'M22 12h-4l-3 9L9 3l-3 9H2'
        };
        if (iconPaths[icon]) path.setAttribute('d', iconPaths[icon]);
    }, 0);
  }

  handleOpenAiStudio() {
    tma.hapticFeedback('medium');
    const existing = document.getElementById('ai-studio-modal-root');
    if (existing) existing.remove();

    const rootEl = document.createElement('div');
    rootEl.id = 'ai-studio-modal-root';
    document.body.appendChild(rootEl);

    aiStudio.init(rootEl, (blueprint: MiniAppBlueprint) => {
      this.handleForgeWithBlueprint(blueprint);
    });
  }

  async handleForgeWithBlueprint(blueprint: MiniAppBlueprint) {
    (window as any).selectedTemplate = blueprint.template_id;
    window.history.pushState({}, '', '/factory');
    this.init();

    setTimeout(() => {
      if (document.getElementById('f-name')) (document.getElementById('f-name') as HTMLInputElement).value = blueprint.name;
      if (document.getElementById('f-symbol')) (document.getElementById('f-symbol') as HTMLInputElement).value = blueprint.currency_name;
      if (document.getElementById('f-bonus')) (document.getElementById('f-bonus') as HTMLInputElement).value = (blueprint.welcome_bonus || 100).toString();
      if (document.getElementById('f-ref')) (document.getElementById('f-ref') as HTMLInputElement).value = (blueprint.referral_reward || 50).toString();
      
      if (blueprint.theme) {
        if (document.getElementById('f-color-1')) (document.getElementById('f-color-1') as HTMLInputElement).value = blueprint.theme.primaryColor || '#6366f1';
        if (document.getElementById('f-color-2')) (document.getElementById('f-color-2') as HTMLInputElement).value = blueprint.theme.secondaryColor || '#a855f7';
        if (document.getElementById('f-color-3')) (document.getElementById('f-color-3') as HTMLInputElement).value = blueprint.theme.accentColor || '#f43f5e';
        if (document.getElementById('f-bg')) (document.getElementById('f-bg') as HTMLInputElement).value = blueprint.theme.bgColor || '#0b0f19';
        if (document.getElementById('f-font')) (document.getElementById('f-font') as HTMLSelectElement).value = blueprint.theme.fontFamily || 'Outfit';
        if (document.getElementById('f-load-grad')) (document.getElementById('f-load-grad') as HTMLInputElement).value = blueprint.theme.loadingGradient || '';
      }

      if (blueprint.loading_config && document.getElementById('f-loading-text')) {
        (document.getElementById('f-loading-text') as HTMLInputElement).value = blueprint.loading_config.text || '';
      }

      if (document.getElementById('f-conv-enabled')) (document.getElementById('f-conv-enabled') as HTMLInputElement).checked = !!blueprint.conversion_enabled;
      if (document.getElementById('f-conv-rate')) (document.getElementById('f-conv-rate') as HTMLInputElement).value = (blueprint.conversion_rate || 0.001).toString();
      if (document.getElementById('f-conv-currency')) (document.getElementById('f-conv-currency') as HTMLInputElement).value = blueprint.conversion_currency || 'USDT';
      if (document.getElementById('f-min-withdraw')) (document.getElementById('f-min-withdraw') as HTMLInputElement).value = (blueprint.min_withdrawal || 500).toString();
      if (document.getElementById('f-mandatory-tasks')) (document.getElementById('f-mandatory-tasks') as HTMLInputElement).value = (blueprint.mandatory_tasks_to_withdraw || 0).toString();

      toast.show('Blueprint synchronized with Project Factory!', 'success');
    }, 400);
  }

  renderError(msg: string) {
    if (!this.root) return;
    this.root.innerHTML = `
      <div class="min-h-screen flex flex-col items-center justify-center p-8 space-y-6 text-center">
        <h2 class="text-2xl font-black italic uppercase tracking-tighter">Archival Conflict</h2>
        <p class="text-xs text-slate-400 font-mono uppercase tracking-widest max-w-xs">${msg}</p>
        <button onclick="window.location.reload()" class="px-8 py-4 bg-white text-black font-black uppercase text-[10px] tracking-widest rounded-full">Retry Uplink</button>
      </div>
    `;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const app = new NexusApp();
  (window as any).nexus = app;
});
